import React, { useState, useEffect, useCallback } from 'react';
import ErpiaSelect from './ErpiaSelect';
import APIService from '../services/api';
import './CRM.css';
import CRMRadial from './crm/CRMRadial';
import CRMNoyau from './crm/CRMNoyau';
import ClientOrgChart from './crm/ClientOrgChart';
import CRMPlacementWizard from './crm/CRMPlacementWizard';
import CRMAutomatismes from './crm/CRMAutomatismes';
import CRMFacturiers from './crm/CRMFacturiers';
import { confirmErpia, alertErpia } from './ConfirmDialog';
import FactureDrawer from './FactureDrawer';
import { ancre } from '../tutoriel/ancres';

// ── CRM v1 : Clients & Missions (Gestion) ────────────────────────────────────
// Le pilier « du besoin à la facture » : fiches clients (contacts), missions
// (client, ressource liée, TJM, période, taux, statut). La mission est la
// source de vérité de la rémunération — le wizard de conversion ressource et
// les timesheets s'y réfèrent (« Selon mission (CRM) »).
// Contrat (toléré absent, 404/501 → bannière « routes à venir ») :
//   GET/POST /clients ; PUT /clients/:id  → { id, nom, secteur?, ville?,
//     contacts: [{ nom, role?, email?, tel? }] }
//   GET/POST /missions ; PUT /missions/:id → { id, titre, client, client_id?,
//     resource_id?, tjm?, date_debut?, date_fin?, taux_activite?, statut }

const BASE = 'https://besoins-tat-production.up.railway.app';
const PLACEMENT_STORAGE_KEY = 'erpia.crm.placement';
const readPlacementOpen = () => {
  if (typeof window === 'undefined') return false;
  try { return JSON.parse(window.sessionStorage.getItem(PLACEMENT_STORAGE_KEY) || 'null')?.open === true; } catch { return false; }
};
const writePlacementOpen = () => {
  if (typeof window === 'undefined') return;
  try { const current = JSON.parse(window.sessionStorage.getItem(PLACEMENT_STORAGE_KEY) || 'null') || {}; window.sessionStorage.setItem(PLACEMENT_STORAGE_KEY, JSON.stringify({ ...current, open: true })); } catch { /* storage unavailable */ }
};
const clearPlacementOpen = () => {
  if (typeof window === 'undefined') return;
  try { window.sessionStorage.removeItem(PLACEMENT_STORAGE_KEY); } catch { /* storage unavailable */ }
};
const BI = ({ icon, style }) => <i className={`bi bi-${icon}`} style={style} />;
const authH = () => ({ Authorization: `Bearer ${APIService.getToken()}` });
const jsonH = () => ({ ...authH(), 'Content-Type': 'application/json' });

const CARD = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '12px' };
const GHOST_BTN = { display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--input-border)', background: 'var(--ghost-bg)', color: 'var(--text)', fontFamily: 'inherit', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer', flex: 'none' };
const PRIMARY_BTN = { display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '10px 15px', borderRadius: '8px', border: 'none', background: 'var(--accent)', color: 'var(--accent-fg)', fontFamily: 'inherit', fontSize: '13px', fontWeight: 600, cursor: 'pointer', flex: 'none' };
const INPUT = { boxSizing: 'border-box', width: '100%', border: '1px solid var(--input-border)', borderRadius: '8px', background: 'var(--input-bg)', padding: '9px 11px', fontFamily: 'inherit', fontSize: '13px', color: 'var(--text-strong)', outline: 'none' };
const LBL = { fontSize: '10.5px', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-3)' };
const Chip = ({ kind = 'mut', title, style, children }) => (
  <span className={`src-chip src-chip--${kind}`} title={title} style={style}>{children}</span>
);
const mono = { fontFamily: "'IBM Plex Mono',monospace" };
const fmtDate = (iso) => { if (!iso) return ''; const t = new Date(iso); return Number.isNaN(t.getTime()) ? iso : t.toLocaleDateString('fr-CH', { day: '2-digit', month: 'short', year: 'numeric' }); };
const initialsOf = (s) => String(s || '?').trim().split(/\s+/).map(w => w[0]).slice(0, 2).join('').toUpperCase() || '?';

// Les jours de la semaine du calendrier CRA, lundi en tête — les mêmes que
// la grille de saisie de Mon espace, pour qu'un mois se relise à l'identique
// des deux côtés (la ressource saisit, l'entreprise valide).
const JOURS_SEMAINE = ['LU', 'MA', 'ME', 'JE', 'VE', 'SA', 'DI'];

const MISSION_STATUTS = [
  ['draft', 'À préparer', 'mut'],
  ['active', 'Active', 'ok'],
  ['terminee', 'Terminée', 'warn'],
];
const missionChip = (s) => MISSION_STATUTS.find(([k]) => k === s) || MISSION_STATUTS[0];

// Facturation libre (mode free) : facture créée à la main depuis le CRM —
// lignes libres, TVA 8.1 % (CH), échéance. Le PDF est généré par le back avec
// les coordonnées bancaires de l'org (IBAN…) et l'adresse du client.
const FACTURE_STATUTS = [
  ['brouillon', 'Brouillon', 'mut'],
  ['envoyee', 'Envoyée', 'warn'],
  ['payee', 'Payée', 'ok'],
  ['en_retard', 'En retard', 'bad'],
];
const factureChip = (s) => FACTURE_STATUTS.find(([k]) => k === s) || FACTURE_STATUTS[0];
// Motifs d'exonération TVA (utilisés seulement quand tva_taux: 0) — envoyés
// dans tva_mention, imprimés à la place de la ligne TVA sur le PDF.
const TVA_MENTIONS = [
  'TVA non applicable — prestation exonérée',
  'Exportation de services — art. 23 LTVA',
  'Prestation exonérée — art. 21 LTVA',
  'Non assujetti à la TVA',
];
const chfAmt = (n) => `${Number(n || 0).toLocaleString('fr-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} CHF`;
// Le tarif d'une mission DANS SON UNITÉ : un TJM au jour, un taux horaire à
// l'heure. Lire `tjm` seul affichait « – » sur toute mission à l'heure.
const tarifMission = (m) => {
  const n = m?.taux_facturation ?? m?.tjm;
  if (n == null || n === '') return '—';
  return `${Number(n).toLocaleString('fr-CH')} CHF/${m?.unite_facturation === 'heure' ? 'h' : 'j'}`;
};
const libelleTarif = (m) => (m?.unite_facturation === 'heure' ? 'Taux horaire' : 'TJM');
// Le statut d'un besoin en mots : le serveur envoie la clé (« encours »).
const BESOIN_STATUTS = { encours: 'En cours', pret: 'Prêt à envoyer', envoye: 'Envoyé', valide: 'Validé', refuse: 'Refusé' };
const plusDays = (n) => { const t = new Date(); t.setDate(t.getDate() + n); return t.toISOString().slice(0, 10); };

// Collection tolérante : 404/501 = routes pas encore en ligne
function useColl(path) {
  const [items, setItems] = useState(undefined); // undefined = chargement
  const [missing, setMissing] = useState(false);
  const load = useCallback(async () => {
    try {
      const r = await fetch(`${BASE}/${path}`, { headers: authH() });
      if (r.status === 404 || r.status === 501) { setMissing(true); setItems([]); return; }
      if (!r.ok) { setItems(prev => (prev === undefined ? [] : prev)); return; }
      const j = await r.json().catch(() => ({}));
      const arr = j.data || j[path] || (Array.isArray(j) ? j : []);
      setItems(Array.isArray(arr) ? arr : []);
    } catch { setItems(prev => (prev === undefined ? [] : prev)); }
  }, [path]);
  useEffect(() => { load(); }, [load]);
  return { items: items === undefined ? null : items, missing, reload: load };
}

const placementListFrom = (payload) => {
  const sources = [payload, payload?.data, payload?.result, payload?.data?.data].filter(Boolean);
  for (const source of sources) {
    if (Array.isArray(source)) return source;
    for (const key of ['lignes', 'flux', 'placements', 'items', 'rows', 'results']) if (Array.isArray(source?.[key])) return source[key];
  }
  return [];
};
const placementStageKey = (flow) => String(flow?.etape ?? flow?.stage ?? flow?.stage_id ?? '').toLowerCase();
const placementStageLabel = (flow) => ({ retenu: 'Candidat retenu', contrat: 'Contrat & signature', ressource: 'Créer la ressource', mission: 'Affecter à une mission', cra_valide: 'CRA validé', facture: 'Facturation' }[placementStageKey(flow)] || flow?.etape || flow?.stage || 'Flux actif');
const placementCandidateName = (flow) => {
  const candidate = flow?.candidat || flow?.candidate;
  if (candidate && typeof candidate === 'object') {
    const name = [candidate.prenom || candidate.first_name, candidate.nom || candidate.last_name].filter(Boolean).join(' ');
    if (name) return name;
    if (candidate.name || candidate.email) return candidate.name || candidate.email;
  }
  return flow?.candidat_name || flow?.candidate_name || flow?.nom_candidat || (typeof flow?.candidat === 'string' ? flow.candidat : null) || 'Candidat';
};
const placementNeedName = (flow) => {
  const besoin = flow?.besoin;
  if (besoin && typeof besoin === 'object') return besoin.titre || besoin.title || 'Besoin sélectionné';
  return flow?.titre_besoin || flow?.besoin_title || (typeof besoin === 'string' ? besoin : null) || 'Besoin sélectionné';
};
const placementSignatureInfo = (flow) => {
  const status = String(flow?.contrat?.statut || flow?.contrat?.status || flow?.candidate_signature?.status || flow?.signature_status || flow?.envelope?.status || flow?.envelope_status || '').toLowerCase();
  if (['completed', 'signed', 'signe', 'signé'].includes(status)) return ['Signée', 'ok'];
  if (['sent', 'envoyé', 'envoyee', 'pending', 'in_progress', 'awaiting_signature'].includes(status)) return ['En attente de signature', 'warn'];
  if (['draft', 'brouillon'].includes(status)) return ['Brouillon', 'mut'];
  return [status || 'Non démarrée', 'mut'];
};
const placementIsClosed = (flow) => ['facture', 'termine', 'terminee', 'completed', 'closed'].includes(placementStageKey(flow));

function CRMPlacementFlowList({ flows, loading, error, deleteError, onReload, onResume, onDelete, onNew, deletingId }) {
      const [query, setQuery] = useState('');
      const [confirmingId, setConfirmingId] = useState(null);
      const normalizedQuery = query.trim().toLocaleLowerCase('fr-FR');
      const visibleFlows = normalizedQuery ? (flows || []).filter((flow) => [placementCandidateName(flow), placementNeedName(flow), placementStageLabel(flow), placementSignatureInfo(flow)[0]].join(' ').toLocaleLowerCase('fr-FR').includes(normalizedQuery)) : flows;
      return (
        <section className="crm-placement-flow-list" aria-labelledby="crm-placement-flow-list-title" style={{ ...CARD, marginTop: '16px', overflow: 'hidden' }}>
          <div className="crm-placement-list-head">
            <div><div style={LBL}>CRM · Placement</div><h2 id="crm-placement-flow-list-title">Placements en cours</h2><p>Rechercher un flux et reprendre le parcours à l’étape actuelle.</p></div>
            <div className="crm-placement-list-tools"><label className="crm-placement-list-search"><BI icon="search" /><input type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Candidat, besoin, étape…" aria-label="Rechercher un placement" /></label><button type="button" onClick={onReload} style={{ ...GHOST_BTN, padding: '7px 10px', fontSize: '12px' }}><BI icon="arrow-repeat" />Actualiser</button>{onNew && <button type="button" onClick={onNew} style={{ ...PRIMARY_BTN, padding: '7px 12px', fontSize: '12px' }}><BI icon="person-check" />Passer un candidat en ressource</button>}</div>
          </div>
          {deleteError && <div role="alert" className="crm-placement-list-error is-inline"><BI icon="exclamation-circle" />{deleteError}</div>}
      {loading && flows == null ? <div className="crm-placement-list-empty"><BI icon="arrow-repeat" style={{ display: 'inline-block', animation: 'ats-spin .7s linear infinite' }} /> Chargement des flux…</div> : error ? <div role="alert" className="crm-placement-list-error"><BI icon="exclamation-circle" />{error}</div> : !visibleFlows?.length ? <div className="crm-placement-list-empty"><BI icon="briefcase" />{normalizedQuery ? 'Aucun placement ne correspond à cette recherche.' : 'Aucun placement en cours.'}</div> : visibleFlows.map((flow, index) => {
            const signature = placementSignatureInfo(flow);
            const confirming = String(confirmingId) === String(flow.id);
            return <div key={flow.id ?? index} className="crm-placement-list-row">
              <div className="crm-placement-list-main">
                <div className="crm-placement-list-avatar">{initialsOf(placementCandidateName(flow))}</div>
                <div className="crm-placement-list-identity"><strong>{placementCandidateName(flow)}</strong><span>{placementNeedName(flow)}</span></div>
                <div className="crm-placement-list-stage"><div style={LBL}>Étape actuelle</div><strong>{placementStageLabel(flow)}</strong></div>
                <Chip kind={signature[1]}><BI icon="pen" />{signature[0]}</Chip>
                <div className="crm-placement-list-actions"><button type="button" className="crm-placement-secondary" onClick={() => { setConfirmingId(null); onResume(flow); }} data-testid={'button-resume-placement-' + flow.id}><BI icon="arrow-right" />Reprendre</button><button type="button" className="crm-placement-delete" onClick={() => setConfirmingId(flow.id)} aria-label={'Supprimer le placement de ' + placementCandidateName(flow)} data-testid={'button-delete-placement-' + flow.id}><BI icon="trash3" />Supprimer</button></div>
              </div>
              {confirming && <div className="crm-placement-delete-confirm" role="alert"><div><strong>Supprimer ce placement ?</strong><span>Le flux de {placementCandidateName(flow)} sera retiré définitivement.</span></div><div><button type="button" className="crm-placement-secondary" onClick={() => setConfirmingId(null)}>Annuler</button><button type="button" className="crm-placement-delete is-confirm" disabled={String(deletingId) === String(flow.id)} onClick={async () => { const deleted = await onDelete(flow); if (deleted) setConfirmingId(null); }}>{String(deletingId) === String(flow.id) ? 'Suppression…' : 'Confirmer la suppression'}</button></div></div>}
            </div>;
          })}
        </section>
      );
    }
    export default function CRM({ showNotification, organisation, onCreateBesoin, onOpenBesoin, factureInitiale, onOpenRessource, onOpenForms, notifications, onOpenNotif, onReadNotif }) {
  const [tab, setTab] = useState('suivi'); // suivi | placements | clients | ressources | missions | factures | noyau | parametrage
  // Le client au centre du cercle (onglet Suivi). Il reste choisi quand on
  // traverse une fiche et qu'on revient : le cercle reprend là où on l'a laissé.
  const [clientCentre, setClientCentre] = useState(null);
  const [noyauFocus, setNoyauFocus] = useState(null);
  const [viewModes, setViewModes] = useState({ clients: 'list', ressources: 'list', missions: 'list', factures: 'list' });
  const viewMode = viewModes[tab] || 'list';
  const setViewMode = (mode) => setViewModes(prev => ({ ...prev, [tab]: mode }));
  const [q, setQ] = useState('');
  const clients = useColl('clients');
  const missions = useColl('missions');
  const factures = useColl('factures');
  const [factureForm, setFactureForm] = useState(null); // nouvelle facture (mode free)
  const [factureOuverteId, setFactureOuverteId] = useState(null); // tiroir facture
  const [filtreSansDest, setFiltreSansDest] = useState(false); // avant campagne : ne montrer que les factures sans destinataire
  // Lien profond /crm/factures/:id (facturation d'un CRA, toast « Ouvrir la
  // facture ») : on ouvre l'onglet et le tiroir dès que la liste est là.
  useEffect(() => {
    if (factureInitiale == null || !Array.isArray(factures.items)) return;
    setTab('factures');
    setFactureOuverteId(factureInitiale);
  }, [factureInitiale, factures.items]); // eslint-disable-line react-hooks/exhaustive-deps
  const [resources, setResources] = useState([]); // liaison mission ↔ ressource
  const [globalSearch, setGlobalSearch] = useState('');
  const [resourceOuverte, setResourceOuverte] = useState(null);
  // Navigation : les listes, puis les fiches PLEINE PAGE. Le client et la
  // mission sont les deux objets qu'on traverse (client → mission → facture,
  // et retour) : un volet latéral ne suffisait plus à les relier.
  const [vue, setVue] = useState({ type: 'liste' }); // { type:'liste' } | { type:'client', id } | { type:'mission', id }
  const [missionForm, setMissionForm] = useState(null); // mission en édition (objet) | 'new'
  const [clientForm, setClientForm] = useState(null);   // nouveau client
  const [placementOpen, setPlacementOpen] = useState(readPlacementOpen);
  const [placementContext, setPlacementContext] = useState(null);
  const [placementFlows, setPlacementFlows] = useState(null);
  const [placementFlowsLoading, setPlacementFlowsLoading] = useState(true);
  const [placementFlowsError, setPlacementFlowsError] = useState('');
  const [placementDeletingId, setPlacementDeletingId] = useState(null);
  const [placementDeleteError, setPlacementDeleteError] = useState('');
  const loadPlacementFlows = useCallback(async () => {
    setPlacementFlowsLoading(true);
    setPlacementFlowsError('');
    try {
      const response = await fetch(`${BASE}/crm/flux`, { headers: authH() });
      if (response.status === 404 || response.status === 501) throw new Error('La liste des placements n’est pas encore disponible côté serveur.');
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || 'Impossible de charger les placements.');
      setPlacementFlows(placementListFrom(payload));
    } catch (placementError) {
      setPlacementFlowsError(placementError.message || 'Impossible de charger les placements.');
      setPlacementFlows([]);
    } finally { setPlacementFlowsLoading(false); }
  }, []);
  useEffect(() => { loadPlacementFlows(); }, [loadPlacementFlows]);
  const openPlacement = (context = null) => { setPlacementContext(context); setPlacementOpen(true); writePlacementOpen(); };
  const closePlacement = () => { setPlacementOpen(false); setPlacementContext(null); clearPlacementOpen(); loadPlacementFlows(); };
      const deletePlacement = useCallback(async (flow) => {
        if (!flow?.id) return false;
        setPlacementDeletingId(flow.id);
        setPlacementFlowsError('');
        try {
          const response = await fetch(`${BASE}/crm/flux/${encodeURIComponent(flow.id)}`, { method: 'DELETE', headers: authH() });
          const payload = await response.json().catch(() => ({}));
          if (!response.ok) throw new Error(payload.error || payload.message || 'Impossible de supprimer ce placement.');
          setPlacementFlows((current) => (current || []).filter((item) => String(item.id) !== String(flow.id)));
          showNotification?.('Placement supprimé', 'success');
          return true;
        } catch (deleteError) {
          const message = deleteError.message || 'Impossible de supprimer ce placement.';
          setPlacementDeleteError(message);
          showNotification?.(message, 'error');
          return false;
        } finally { setPlacementDeletingId(null); }
      }, [showNotification]);
  const placementRows = (placementFlows || []).filter(flow => flow?.id != null && !placementIsClosed(flow));

  const chargerResources = useCallback(() => fetch(`${BASE}/resources`, { headers: authH() })
    .then(r => (r.ok ? r.json() : {}))
    .then(j => { const arr = j.data || j.resources || (Array.isArray(j) ? j : []); setResources(Array.isArray(arr) ? arr : []); })
    .catch(() => {}), []);
  useEffect(() => { chargerResources(); }, [chargerResources]);

  // Sélection multiple de la liste des ressources — la suppression est le seul
  // geste qui la consomme, et elle passe toujours par une confirmation.
  const [resSelection, setResSelection] = useState(() => new Set());
  const [resSuppression, setResSuppression] = useState(false);
  const basculerRes = (id) => setResSelection(prev => {
    const n = new Set(prev); const k = String(id);
    if (n.has(k)) n.delete(k); else n.add(k);
    return n;
  });

  const resourceLabel = (r) => [r?.prenom || r?.first_name, r?.nom || r?.last_name].filter(Boolean).join(' ') || r?.name || r?.email || 'Ressource';

  /**
   * Suppression de ressources.
   *
   * Une ressource qui a une mission, un contrat ou des CRA ne se supprime pas
   * à l'aveugle : c'est le SERVEUR qui connaît ses attaches et qui refuse
   * (409), avec un message écrit pour la personne. Le front ne devine rien —
   * il montre ce refus tel quel, puis propose de forcer, et ce n'est qu'alors
   * que la suppression emporte les dépendances (?cascade=1).
   */
  const executerSuppressionRes = async (ids, cascade) => {
    const bloques = []; const erreurs = []; let absents = 0; let faits = 0;
    for (const id of ids) {
      try {
        const url = `${BASE}/resources/${encodeURIComponent(id)}${cascade ? '?cascade=1' : ''}`;
        const r = await fetch(url, { method: 'DELETE', headers: authH() });
        const j = await r.json().catch(() => ({}));
        if (r.ok && j.success !== false) { faits += 1; continue; }
        const msg = j.error || j.message || '';
        if ([404, 405, 501].includes(r.status) && !msg) { absents += 1; continue; }
        // 409 = attaches existantes : le serveur dit lesquelles, on le répète.
        if (r.status === 409 || (r.status === 400 && msg)) bloques.push({ id, nom: resName(id) || `Ressource ${id}`, message: msg });
        else erreurs.push(`${resName(id) || id} : ${msg || `HTTP ${r.status}`}`);
      } catch (e) { erreurs.push(`${resName(id) || id} : ${e.message}`); }
    }
    return { faits, bloques, erreurs, absents };
  };

  const supprimerRessourcesSelection = async () => {
    const ids = [...resSelection];
    if (!ids.length || resSuppression) return;
    const noms = ids.map(id => resName(id) || `Ressource ${id}`);
    // Ce que le front sait déjà : les missions qu'il a en mémoire. Le serveur
    // complétera (contrats, CRA, factures) — on ne prétend pas les connaître.
    const liees = (missions.items || []).filter(m => ids.some(id => String(m.resource_id ?? m.resourceId ?? m.ressource_id ?? '') === String(id)));
    const ok = await confirmErpia({
      title: ids.length > 1 ? `Supprimer ${ids.length} ressources ?` : 'Supprimer la ressource ?',
      message: noms.join(', '),
      detail: liees.length
        ? `${liees.length} mission${liees.length > 1 ? 's' : ''} rattachée${liees.length > 1 ? 's' : ''} : ${liees.map(m => m.titre || m.title || `Mission ${m.id}`).join(', ')}. Le serveur vérifiera aussi les contrats et les CRA avant de supprimer.`
        : 'Le serveur vérifiera ses missions, contrats et CRA avant de supprimer.',
      confirmLabel: ids.length > 1 ? 'Supprimer les ressources' : 'Supprimer la ressource',
    });
    if (!ok) return;
    setResSuppression(true);
    try {
      let bilan = await executerSuppressionRes(ids, false);
      if (bilan.bloques.length) {
        const forcer = await confirmErpia({
          title: 'Ces ressources sont rattachées',
          // Le serveur nomme souvent la ressource dans sa phrase : on ne la
          // répète pas devant.
          message: bilan.bloques.map(b => (b.message.includes(b.nom) ? b.message : `${b.nom} — ${b.message}`)).join('\n'),
          detail: 'Supprimer quand même emporte tout ce qui en dépend : missions, contrats et CRA rattachés. C\'est définitif.',
          confirmLabel: 'Supprimer avec les dépendances',
        });
        if (forcer) {
          const second = await executerSuppressionRes(bilan.bloques.map(b => b.id), true);
          bilan = {
            faits: bilan.faits + second.faits,
            bloques: second.bloques,
            erreurs: [...bilan.erreurs, ...second.erreurs],
            absents: bilan.absents + second.absents,
          };
        }
      }
      if (bilan.faits) showNotification?.(bilan.faits > 1 ? `${bilan.faits} ressources supprimées` : 'Ressource supprimée', 'success');
      if (bilan.absents) showNotification?.('La suppression de ressources arrive côté serveur — rien n\'a été supprimé.', 'info');
      bilan.bloques.forEach(b => showNotification?.(`${b.nom} : ${b.message}`, 'error'));
      bilan.erreurs.forEach(m => showNotification?.(m, 'error'));
      setResSelection(new Set());
      await chargerResources();
      missions.reload?.();
    } finally { setResSuppression(false); }
  };
  const resName = (id) => { const r = resources.find(x => String(x.id) === String(id)); return r ? resourceLabel(r) : null; };
  const clientName = (m) => m.client || (clients.items || []).find(c => String(c.id) === String(m.client_id))?.nom || '—';
  const s = q.trim().toLowerCase();
  const clientRows = (clients.items || []).filter(c => !s || `${c.nom} ${c.secteur || ''} ${c.ville || ''}`.toLowerCase().includes(s));
  const missionRows = (missions.items || []).filter(m => !s || `${m.titre} ${clientName(m)} ${resName(m.resource_id) || ''}`.toLowerCase().includes(s));
  const missionsOf = (c) => (missions.items || []).filter(m => String(m.client_id) === String(c.id) || (m.client && c.nom && m.client.trim().toLowerCase() === c.nom.trim().toLowerCase()));
  // Le serveur pose destinataire: null sur les lignes sans destinataire résolu.
  // Clé absente = serveur muet : on ne marque rien plutôt que de tout marquer.
  const sansDestinataire = (f) => Object.prototype.hasOwnProperty.call(f || {}, 'destinataire') && !f.destinataire;
  const factureSansDest = (factures.items || []).filter(sansDestinataire).length;
  const factureRows = (factures.items || [])
    .filter(f => !s || `${f.numero || ''} ${f.client || ''}`.toLowerCase().includes(s))
    .filter(f => !filtreSansDest || sansDestinataire(f));
  const resourceRows = resources.filter(r => !s || `${resourceLabel(r)} ${Object.values(r || {}).join(' ')}`.toLowerCase().includes(s));

  const normalizedGlobal = globalSearch.trim().toLowerCase();
  const searchable = (value) => Object.values(value || {}).join(' ').toLowerCase();
  // Les ACTIONS se cherchent comme le reste — taper « placement », « ressource »
  // ou « contrat » propose le parcours guidé, sans bouton posé dans l'en-tête.
  const ACTIONS_CRM = [
    { id: 'placement', label: 'Passer un candidat en ressource', meta: 'Contrat, signature signd, création de la ressource et de sa mission — le parcours guidé', icon: 'person-check', mots: 'placement placer preparer candidat ressource contrat signature signd embauche embaucher transformer convertir retenu mission' },
  ];
  const globalResults = normalizedGlobal ? [
    ...ACTIONS_CRM.filter(a => `${a.label} ${a.mots}`.toLowerCase().includes(normalizedGlobal))
      .map(a => ({ type: 'action', id: a.id, label: a.label, meta: a.meta, icon: a.icon, item: a })),
    ...(clients.items || []).filter(c => searchable(c).includes(normalizedGlobal)).slice(0, 4).map(c => ({ type: 'client', id: c.id, label: c.nom || 'Client', meta: [c.secteur, c.ville].filter(Boolean).join(' · ') || 'Compte client', icon: 'buildings', item: c })),
    ...resources.filter(r => searchable(r).includes(normalizedGlobal) || resourceLabel(r).toLowerCase().includes(normalizedGlobal)).slice(0, 4).map(r => ({ type: 'resource', id: r.id, label: resourceLabel(r), meta: r.role || r.poste || r.fonction || r.email || 'Ressource', icon: 'person-badge', item: r })),
    ...(missions.items || []).filter(m => searchable(m).includes(normalizedGlobal) || clientName(m).toLowerCase().includes(normalizedGlobal) || String(resName(m.resource_id) || '').toLowerCase().includes(normalizedGlobal)).slice(0, 4).map(m => ({ type: 'mission', id: m.id, label: m.titre || 'Mission', meta: [clientName(m), resName(m.resource_id)].filter(Boolean).join(' · '), icon: 'briefcase', item: m })),
    ...(factures.items || []).filter(f => searchable(f).includes(normalizedGlobal)).slice(0, 4).map(f => ({ type: 'facture', id: f.id, label: f.numero || 'Facture', meta: [f.client, f.statut].filter(Boolean).join(' · '), icon: 'receipt', item: f })),
  ].slice(0, 10) : [];

  // Une mission ouverte en édition : le même formulaire depuis la fiche et
  // depuis le cercle (« Assigner une mission »). Les dates arrivent horodatées
  // (« 2026-10-15T00:00:00.000Z ») : telles quelles, le champ date s'affichait
  // VIDE, et enregistrer renvoyait null — la mission perdait ses dates.
  const formMission = (m) => ({ ...m, client_id: m.client_id ?? '', resource_id: m.resource_id ?? '', unite_facturation: m.unite_facturation || 'jour', tjm: m.taux_facturation ?? m.tjm ?? '', taux_activite: m.taux_activite ?? '', date_debut: String(m.date_debut || '').slice(0, 10), date_fin: String(m.date_fin || '').slice(0, 10) });
  const nouvelleMission = (client) => ({ titre: '', client: client?.nom || '', client_id: client?.id ?? '', resource_id: '', unite_facturation: 'jour', tjm: '', date_debut: '', date_fin: '', taux_activite: '100', statut: 'draft' });
  const newFactureForm = () => ({ client: '', client_id: '', client_adresse: '', lignes: [{ description: '', quantite: '1', prix_unitaire: '' }], tva_taux: '8.1', echeance: plusDays(30), note: '' });
  const openNewFacture = () => { setTab('factures'); setQ(''); setFactureForm(newFactureForm()); };
  const openGlobalResult = (result) => {
    setGlobalSearch('');
    if (result.type === 'action') { setGlobalSearch(''); if (result.id === 'placement') openPlacement(); return; }
    if (result.type === 'client') { setTab('clients'); setQ(''); setVue({ type: 'client', id: result.id }); }
    if (result.type === 'mission') { setTab('missions'); setQ(''); setVue({ type: 'mission', id: result.id }); }
    if (result.type === 'resource') setResourceOuverte(result.item);
    if (result.type === 'facture') { setTab('factures'); setQ(result.label); }
  };
  const onGlobalSearch = (value) => { setGlobalSearch(value); if (['clients', 'missions', 'factures'].includes(tab)) setQ(value); };

  // Statut d'une facture (brouillon → envoyée → payée / en retard)
  const setFactureStatut = async (f, statut) => {
    try {
      const r = await fetch(`${BASE}/factures/${f.id}`, { method: 'PUT', headers: jsonH(), body: JSON.stringify({ statut }) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      factures.reload();
    } catch (e) { showNotification?.('Erreur statut : ' + e.message, 'error'); }
  };
  // PDF de la facture (généré par le back avec IBAN + coordonnées org + client)
  const dlFacture = async (f) => {
    try {
      const url = f.pdf_url || `${BASE}/factures/${f.id}.pdf`;
      const r = await fetch(url, { headers: authH() });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const u = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = u; a.download = `${f.numero || 'facture'}.pdf`; a.click();
      URL.revokeObjectURL(u);
    } catch (e) { showNotification?.('PDF indisponible : ' + e.message, 'error'); }
  };

  const missing = clients.missing || missions.missing;
  const loading = clients.items === null || missions.items === null;

  // Objets ouverts : on les relit dans les listes rechargées, jamais figés
  // dans l'état de navigation (sinon un enregistrement n'apparaîtrait pas).
  const clientOuvert = vue.type === 'client' ? (clients.items || []).find(c => String(c.id) === String(vue.id)) : null;
  const missionOuverte = vue.type === 'mission' ? (missions.items || []).find(m => String(m.id) === String(vue.id)) : null;
  const clientDeMission = (m) => (clients.items || []).find(c => String(c.id) === String(m?.client_id))
    || (m?.client ? (clients.items || []).find(c => (c.nom || '').trim().toLowerCase() === m.client.trim().toLowerCase()) : null);
  const facturesDeMission = (m) => (factures.items || []).filter(f => m?.id != null && String(f.mission_id ?? f.missionId ?? '') === String(m.id));

  // Fiche client pleine page
  if (vue.type === 'client' && clientOuvert) {
    return (
      <ClientPage client={clientOuvert} missions={missionsOf(clientOuvert)} resName={resName}
        showNotification={showNotification} onSaved={() => clients.reload()} onCreateBesoin={() => onCreateBesoin?.(clientOuvert)}
        onRetour={() => setVue({ type: 'liste' })}
        onSupprime={() => { setVue({ type: 'liste' }); clients.reload(); }}
        onOuvrirMission={(m) => setVue({ type: 'mission', id: m.id })} />
    );
  }
  // Fiche mission pleine page
  if (vue.type === 'mission' && missionOuverte) {
    const cl = clientDeMission(missionOuverte);
    return (
      <>
        <MissionPage mission={missionOuverte} client={cl} resName={resName}
          factures={facturesDeMission(missionOuverte)}
          onRetour={() => setVue({ type: 'liste' })}
          onOuvrirClient={() => cl && setVue({ type: 'client', id: cl.id })}
          onOuvrirBesoin={onOpenBesoin}
          onOuvrirCra={() => { setVue({ type: 'liste' }); setTab('cra'); }}
          showNotification={showNotification}
          onSupprimee={() => { setVue({ type: 'liste' }); missions.reload(); }}
          onEditer={() => setMissionForm(formMission(missionOuverte))} />
        {missionForm && <MissionModal form={missionForm} setForm={setMissionForm} clients={clients.items || []} resources={resources}
          showNotification={showNotification} onDone={() => { setMissionForm(null); missions.reload(); }} />}
      </>
    );
  }

  return (
    <div className="crm-view">
      <div className="crm-heading">
        <div className="crm-heading-copy">
          <div className="crm-eyebrow">Relation client <span>ERP</span></div>
          <h1 className="crm-title" aria-label="CRM — Clients, Relationnel, Missions"><span className="crm-title-letter">C</span><span className="crm-title-letter">R</span><span className="crm-title-letter">M</span><em>.</em></h1>
          <div className="crm-acronym" aria-label="Signification de CRM"><span><strong>C</strong>Clients</span><span><strong>R</strong>Relationnel</span><span><strong>M</strong>Missions</span></div>
          <p className="crm-subtitle">Comptes, missions et facturation — une vue unique du cycle client.</p>
        </div>
        {tab === 'suivi' && (
          <span className="crm-heading-date"><BI icon="calendar3" />{new Date().toLocaleDateString('fr-CH', { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' })}</span>
        )}
      </div>

      {placementOpen && <CRMPlacementWizard key={placementContext?.fluxId || 'new-placement'} initialFluxId={placementContext?.fluxId || ''} onClose={closePlacement} showNotification={showNotification} missions={missions.items || []} clients={clients.items || []} />}

      <div className="crm-toolbar">
        <div className="crm-tab-list" role="tablist" aria-label="Sections CRM" {...ancre('crm-onglets')}>
            <button className="crm-tab" role="tab" aria-selected={tab === 'suivi'} onClick={() => { setTab('suivi'); setQ(''); }}><BI icon="list-task" />Suivi</button>
            <button className="crm-tab" role="tab" aria-selected={tab === 'placements'} onClick={() => { setTab('placements'); setQ(''); }}><BI icon="briefcase" />Placements · {placementRows.length}</button>
            <button className="crm-tab" role="tab" aria-selected={tab === 'clients'} onClick={() => setTab('clients')}><BI icon="buildings" />Clients · {(clients.items || []).length}</button>
            <button className="crm-tab" role="tab" aria-selected={tab === 'ressources'} onClick={() => setTab('ressources')}><BI icon="person-badge" />Ressources · {resources.length}</button>
            <button className="crm-tab" role="tab" aria-selected={tab === 'missions'} onClick={() => setTab('missions')}><BI icon="briefcase" />Missions · {(missions.items || []).length}</button>
            <button className="crm-tab" role="tab" aria-selected={tab === 'cra'} onClick={() => { setTab('cra'); setQ(''); }}><BI icon="calendar2-check" />CRA</button>
            <button className="crm-tab" role="tab" aria-selected={tab === 'factures'} onClick={() => setTab('factures')}><BI icon="receipt" />Factures · {(factures.items || []).length}</button>
            <button className="crm-tab" role="tab" aria-selected={tab === 'noyau'} onClick={() => { setNoyauFocus(null); setTab('noyau'); setQ(''); }}><BI icon="diagram-3" />Noyau relationnel</button>
            {/* La pastille « bientôt » est tombée avec la mise en ligne du
                module : un onglet qui s'annonce alors qu'il fonctionne fait
                douter de tout le reste de l'écran. */}
            <button className="crm-tab" role="tab" aria-selected={tab === 'automatismes'} onClick={() => { setTab('automatismes'); setQ(''); }}><BI icon="arrow-return-right" />Automatismes</button>
            {/* Le paramétrage de la facturation vit ici, à côté des factures
                qu'il gouverne — plus dans la Configuration générale. */}
            <button className="crm-tab" role="tab" aria-selected={tab === 'parametrage'} onClick={() => { setTab('parametrage'); setQ(''); }}><BI icon="sliders" />Paramètres</button>
            </div>
        <div className="crm-toolbar-actions">
            {['clients', 'ressources', 'missions', 'factures'].includes(tab) && <CRMViewToggle mode={viewMode} onChange={setViewMode} />}
            {['clients', 'ressources', 'missions', 'factures'].includes(tab) && <label className="crm-search" aria-label="Rechercher dans le CRM"><BI icon="search" style={{ fontSize: '12px' }} /><input value={q} onChange={e => setQ(e.target.value)} placeholder="Rechercher…" style={INPUT} /></label>}
            {tab === 'clients' && <button onClick={() => setClientForm({ nom: '', secteur: '', ville: '' })} style={{ ...PRIMARY_BTN, padding: '9px 14px' }}><BI icon="plus-lg" style={{ fontSize: '12px' }} />Nouveau client</button>}
            {tab === 'missions' && <button onClick={() => setMissionForm(nouvelleMission(null))} style={{ ...PRIMARY_BTN, padding: '9px 14px' }}><BI icon="plus-lg" style={{ fontSize: '12px' }} />Nouvelle mission</button>}
            {tab === 'factures' && <button onClick={openNewFacture} style={{ ...PRIMARY_BTN, padding: '9px 14px' }}><BI icon="plus-lg" style={{ fontSize: '12px' }} />Nouvelle facture</button>}
          </div>
      </div>

      {tab !== 'suivi' && (
        <CRMCommandCenter organisation={organisation} query={globalSearch} loading={loading} results={globalResults}
          onQuery={onGlobalSearch} onSelect={openGlobalResult} onNewFacture={openNewFacture} onNewPlacement={() => openPlacement()}
          onOpenClients={() => { setTab('clients'); setQ(''); }} onOpenMissions={() => { setTab('missions'); setQ(''); }}
          onOpenFactures={() => setTab('factures')} onOpenResources={() => { setTab('ressources'); setQ(''); }} onOpenNoyau={() => { setNoyauFocus(null); setTab('noyau'); setQ(''); }} />
      )}

      {missing && (
        <div style={{ marginTop: '16px', padding: '10px 14px', borderRadius: '9px', background: 'var(--code-bg)', border: '1px solid var(--hair)', color: 'var(--text-2)', fontSize: '12.5px' }}>
          <BI icon="hourglass-split" style={{ marginRight: '7px' }} />Les routes <code style={mono}>/clients</code> et <code style={mono}>/missions</code> arrivent côté serveur — l'écran est prêt, vos saisies aboutiront dès leur mise en ligne.
        </div>
      )}

      {loading ? (
        <div style={{ ...CARD, marginTop: '16px', padding: '32px', textAlign: 'center', color: 'var(--text-faint)', fontSize: '13px' }}>
          <BI icon="arrow-repeat" style={{ display: 'inline-block', animation: 'ats-spin .7s linear infinite' }} /> Chargement…
        </div>
      ) : tab === 'suivi' ? (
          <CRMRadial clients={clients.items || []} missions={missions.items || []} factures={factures.items || []} resources={resources}
            selectionId={clientCentre} onSelection={setClientCentre}
            recherche={globalSearch} resultats={globalResults} onRecherche={onGlobalSearch} onResultat={openGlobalResult}
            onNouveauClient={(nom) => setClientForm({ nom: typeof nom === 'string' ? nom : '', secteur: '', ville: '' })}
            onCreerMission={(c) => setMissionForm(nouvelleMission(c))}
            onAssigner={(m, r) => setMissionForm({ ...formMission(m), ...(r ? { resource_id: r.id } : {}) })}
            onPlacer={() => openPlacement()}
            onFacturer={(c) => setFactureForm({ ...newFactureForm(), client: c?.nom || '', client_id: c?.id ?? '' })}
            onCra={() => { setTab('cra'); setQ(''); }}
            onFormulaires={onOpenForms}
            onNoyau={(c) => { setNoyauFocus(c?.id ?? null); setTab('noyau'); setQ(''); }}
            onFicheClient={(c) => setVue({ type: 'client', id: c.id })}
            onOuvrirFacture={(f) => setFactureOuverteId(f.id)}
            onOuvrirMission={(m) => setVue({ type: 'mission', id: m.id })}
            onOuvrirRessource={(r) => setResourceOuverte(r)}
            onVoir={(cible, c) => { setTab(cible); setQ(c?.nom || ''); }}
            notifications={notifications || []} onOuvrirNotif={onOpenNotif} onLireNotif={onReadNotif} />
        ) : tab === 'placements' ? (
          <CRMPlacementFlowList flows={placementRows} loading={placementFlowsLoading} error={placementFlowsError} deleteError={placementDeleteError} onReload={loadPlacementFlows} onResume={(flow) => openPlacement({ fluxId: flow.id })} onNew={() => openPlacement()} onDelete={deletePlacement} deletingId={placementDeletingId} />
        ) : tab === 'automatismes' ? (
          <CRMAutomatismes showNotification={showNotification} />
        ) : tab === 'parametrage' ? (
          <CRMParametrage showNotification={showNotification} />
        ) : tab === 'cra' ? (
          <CRMCRA showNotification={showNotification} onFactureCreated={factures.reload} />
        ) : tab === 'ressources' ? (
          <>
            {resSelection.size > 0 && (
              <div className="crm-selection-bar" role="status">
                <span className="crm-mono">{resSelection.size} ressource{resSelection.size > 1 ? 's' : ''} sélectionnée{resSelection.size > 1 ? 's' : ''}</span>
                <span style={{ flex: 1 }} />
                <button type="button" className="erp-btn erp-btn--secondary erp-btn--sm" onClick={() => setResSelection(new Set())}>Tout désélectionner</button>
                <button type="button" className="erp-btn erp-btn--danger erp-btn--sm" onClick={supprimerRessourcesSelection} disabled={resSuppression}>
                  <BI icon={resSuppression ? 'arrow-repeat' : 'trash3'} style={resSuppression ? { animation: 'ats-spin .7s linear infinite' } : undefined} />
                  {resSuppression ? 'Suppression…' : 'Supprimer'}
                </button>
              </div>
            )}
            <CRMResourceCollection resources={resourceRows} missions={missions.items || []} mode={viewMode} onSelect={setResourceOuverte}
              selection={resSelection} onToggle={basculerRes} />
          </>
        ) : tab === 'noyau' ? (
          <CRMNoyau key={noyauFocus ?? 'organisation'} focusClientId={noyauFocus} organisation={organisation} clients={clients.items || []} missions={missions.items || []} factures={factures.items || []} resources={resources}
            onOpenClient={(c) => setVue({ type: 'client', id: c.id })} onOpenMission={(m) => setVue({ type: 'mission', id: m.id })} />
        ) : tab === 'clients' ? (
        <>
          {viewMode === 'cards' ? <CRMClientCards clients={clientRows} missionsOf={missionsOf} onOpen={(c) => setVue({ type: 'client', id: c.id })} /> : <div className="crm-list-card">
          {clientRows.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-faint)', fontSize: '13px' }}>
              <BI icon="buildings" style={{ fontSize: '26px', display: 'block', marginBottom: '8px' }} />
              Aucun client — créez la première fiche avec « Nouveau client ».
            </div>
          ) : clientRows.map((c, i) => {
            const ms = missionsOf(c);
            const actives = ms.filter(m => m.statut === 'active').length;
            return (
              <div key={c.id ?? i} onClick={() => setVue({ type: 'client', id: c.id })} className="crm-list-row src-row-hover"
                style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 18px', borderBottom: '1px solid var(--hair)', cursor: 'pointer', flexWrap: 'wrap' }}>
                <div style={{ width: '38px', height: '38px', borderRadius: '10px', background: 'var(--text-strong)', color: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', ...mono, fontSize: '12px', fontWeight: 600, flex: 'none' }}>{initialsOf(c.nom)}</div>
                <div style={{ flex: 1.4, minWidth: '170px' }}>
                  <div style={{ fontSize: '14px', fontWeight: 700, letterSpacing: '-0.01em', color: 'var(--text-strong)' }}>{c.nom}</div>
                  <div style={{ marginTop: '1px', fontSize: '12px', color: 'var(--text-3)' }}>{[c.secteur, c.ville].filter(Boolean).join(' · ') || '—'}</div>
                </div>
                <Chip kind="mut"><BI icon="person-lines-fill" style={{ fontSize: '10px' }} /> {(c.contacts || []).length} contact{(c.contacts || []).length > 1 ? 's' : ''}</Chip>
                <Chip kind={actives ? 'ok' : 'mut'}><BI icon="briefcase" style={{ fontSize: '10px' }} /> {actives} mission{actives > 1 ? 's' : ''} active{actives > 1 ? 's' : ''}</Chip>
                <BI icon="chevron-right" style={{ fontSize: '12px', color: 'var(--text-faint)', flex: 'none' }} />
              </div>
            );
          })}
          </div>}
        </>
      ) : tab === 'factures' ? (
        <>
          <FacturationRappel onOuvrir={() => setTab('parametrage')} />
          {/* Avant une campagne d'envoi : ce qui bloquera, dit une fois, avec
              le moyen de n'afficher que ces factures-là. */}
          {factureSansDest > 0 && (
            <div style={{ marginTop: '14px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', padding: '9px 13px', borderRadius: '9px', border: '1px solid color-mix(in srgb,var(--warn) 45%,transparent)', background: 'color-mix(in srgb,var(--warn) 7%,transparent)', color: 'var(--warn)', fontSize: '11.5px', fontWeight: 600 }}>
              <BI icon="envelope-exclamation" />
              <span style={{ flex: 1 }}>{factureSansDest} facture{factureSansDest > 1 ? 's' : ''} sans destinataire — l'adresse sera demandée à l'envoi.</span>
              <button onClick={() => setFiltreSansDest(v => !v)} className="erp-btn erp-btn--secondary erp-btn--sm" aria-pressed={filtreSansDest}>
                {filtreSansDest ? 'Voir toutes les factures' : 'Ne montrer que celles-là'}
              </button>
            </div>
          )}
          {viewMode === 'cards' ? <CRMFactureCards factures={factureRows} missions={missions.items || []} onOpen={(f) => setFactureOuverteId(f.id)} onOpenMission={(m) => setVue({ type: 'mission', id: m.id })} onStatus={setFactureStatut} onDownload={dlFacture} /> : <div style={{ ...CARD, marginTop: '14px', overflow: 'hidden' }}>
            {factures.items === null ? (
              <div style={{ padding: '32px', textAlign: 'center', color: 'var(--text-faint)', fontSize: '13px' }}>
                <BI icon="arrow-repeat" style={{ display: 'inline-block', animation: 'ats-spin .7s linear infinite' }} /> Chargement…
              </div>
            ) : factureRows.length === 0 ? (
              <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-faint)', fontSize: '13px' }}>
                <BI icon="receipt" style={{ fontSize: '26px', display: 'block', marginBottom: '8px' }} />
                {factures.missing ? 'Les routes /factures arrivent côté serveur — créez quand même, l\'écran est prêt.' : 'Aucune facture — créez la première avec « Nouvelle facture ».'}
              </div>
            ) : factureRows.map((f, i) => {
              const [, sl, sk] = factureChip(f.statut);
              return (
                <div key={f.id ?? i} onClick={() => setFactureOuverteId(f.id)} className="src-row-hover" role="button" tabIndex={0}
                  onKeyDown={(ev) => { if (ev.key === 'Enter') setFactureOuverteId(f.id); }}
                  style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 18px', borderBottom: '1px solid var(--hair)', flexWrap: 'wrap', cursor: 'pointer' }}>
                  <span style={{ ...mono, fontSize: '12.5px', fontWeight: 700, color: 'var(--text-strong)', flex: 'none', width: '100px' }}>{f.numero || `#${f.id}`}</span>
                  <div style={{ flex: 1.4, minWidth: '150px' }}>
                    <div style={{ fontSize: '13.5px', fontWeight: 600, color: 'var(--text-strong)' }}>{f.client || '—'}</div>
                    {/* D'où vient la facture : la mission (cliquable) et le mois
                        de relevé facturé, quand le serveur les rattache. */}
                    <div style={{ marginTop: '1px', fontSize: '11.5px', color: 'var(--text-3)', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                      {(() => {
                        const mid = f.mission_id ?? f.missionId ?? null;
                        const mi = mid != null ? (missions.items || []).find(x => String(x.id) === String(mid)) : null;
                        return mi ? (
                          <button onClick={(ev) => { ev.stopPropagation(); setVue({ type: 'mission', id: mi.id }); }}
                            style={{ border: 'none', background: 'none', padding: 0, cursor: 'pointer', fontFamily: 'inherit', fontSize: '11.5px', fontWeight: 600, color: 'var(--text-2)', textDecoration: 'underline' }}
                            title="Ouvrir la mission d'origine">{mi.titre}</button>
                        ) : null;
                      })()}
                      {(f.cra_month || f.mois) && <span style={mono}>· temps {f.cra_month || f.mois}</span>}
                      <span>{f.echeance ? `· échéance ${fmtDate(f.echeance)}` : ''}</span>
                      {sansDestinataire(f) && <span style={{ color: 'var(--warn)', fontWeight: 600 }}>· sans destinataire</span>}
                    </div>
                  </div>
                  <span style={{ ...mono, fontSize: '12px', color: 'var(--text-3)', flex: 'none' }} title="Montant HT">{chfAmt(f.total_ht)}</span>
                  <span style={{ ...mono, fontSize: '12px', color: 'var(--text-3)', flex: 'none' }} title={`TVA ${f.tva_taux ?? 8.1} %`}>+ {chfAmt(f.tva_montant ?? (Number(f.total_ht || 0) * (Number(f.tva_taux ?? 8.1) / 100)))}</span>
                  <span style={{ ...mono, fontSize: '13px', fontWeight: 700, color: 'var(--text-strong)', flex: 'none', width: '120px', textAlign: 'right' }}>{chfAmt(f.total_ttc ?? (Number(f.total_ht || 0) * (1 + Number(f.tva_taux ?? 8.1) / 100)))}</span>
                  <ErpiaSelect value={f.statut || 'brouillon'} onChange={e => setFactureStatut(f, e.target.value)} onClick={e => e.stopPropagation()}
                    style={{ ...INPUT, width: 'auto', padding: '5px 8px', fontSize: '11.5px', flex: 'none' }} title="Changer le statut">
                    {FACTURE_STATUTS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                  </ErpiaSelect>
                  <Chip kind={sk}>{sl}</Chip>
                  <button onClick={(ev) => { ev.stopPropagation(); dlFacture(f); }} className="src-row-hover" title="Télécharger le PDF (IBAN et coordonnées inclus)"
                    style={{ ...GHOST_BTN, padding: '6px 10px', fontSize: '12px' }}><BI icon="download" /> PDF</button>
                </div>
              );
            })}
          </div>}
        </>
      ) : (
        <>
        {viewMode === 'cards' ? <CRMMissionCards missions={missionRows} clientName={clientName} resName={resName} onOpen={(m) => setVue({ type: 'mission', id: m.id })} /> : <div className="crm-list-card">
          {missionRows.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-faint)', fontSize: '13px' }}>
              <BI icon="briefcase" style={{ fontSize: '26px', display: 'block', marginBottom: '8px' }} />
              Aucune mission — créez-en une avec « Nouvelle mission ».
            </div>
          ) : missionRows.map((m, i) => {
            const [, sl, sk] = missionChip(m.statut);
            const rn = resName(m.resource_id);
            return (
              <div key={m.id ?? i} onClick={() => setVue({ type: 'mission', id: m.id })} className="crm-list-row src-row-hover"
                style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 18px', borderBottom: '1px solid var(--hair)', cursor: 'pointer', flexWrap: 'wrap' }}>
                <div style={{ flex: 1.5, minWidth: '180px' }}>
                  <div style={{ fontSize: '14px', fontWeight: 700, letterSpacing: '-0.01em', color: 'var(--text-strong)' }}>{m.titre || '—'}</div>
                  <div style={{ marginTop: '1px', fontSize: '12px', color: 'var(--text-3)' }}>{clientName(m)}{rn ? ` · ${rn}` : ''}</div>
                </div>
                <span style={{ ...mono, fontSize: '12.5px', fontWeight: 600, color: 'var(--text-strong)', flex: 'none', width: '110px', textAlign: 'right' }}>{tarifMission(m)}</span>
                <span style={{ fontSize: '12px', color: 'var(--text-3)', flex: 'none', minWidth: '150px' }}>{[fmtDate(m.date_debut), fmtDate(m.date_fin)].filter(Boolean).join(' → ') || '—'}</span>
                {m.taux_activite != null && m.taux_activite !== '' && <span style={{ ...mono, fontSize: '12px', color: 'var(--text-3)', flex: 'none' }}>{m.taux_activite}%</span>}
                <Chip kind={sk}>{sl}</Chip>
              </div>
            );
          })}
        </div>}
        </>
      )}

      {resourceOuverte && <CRMResourceDrawer resource={resourceOuverte} missions={missions.items || []} clients={clients.items || []} onClose={() => setResourceOuverte(null)} onOpenFiche={onOpenRessource} onOpenMission={(m) => { setResourceOuverte(null); setTab('missions'); setVue({ type: 'mission', id: m.id }); }} />}
      {clientForm && <ClientModal form={clientForm} setForm={setClientForm} showNotification={showNotification}
        onDone={() => { setClientForm(null); clients.reload(); }}
        onOuvrirExistant={(c) => { setClientForm(null); setTab('clients'); setQ(''); setVue({ type: 'client', id: c.id }); }} />}
      {factureOuverteId != null && (() => {
        const fo = (factures.items || []).find(x => String(x.id) === String(factureOuverteId));
        if (!fo) return null;
        return <FactureDrawer facture={fo} missions={missions.items || []} clients={clients.items || []} showNotification={showNotification}
          onClose={() => setFactureOuverteId(null)} onChanged={factures.reload} onClientsChanged={clients.reload}
          onOpenMission={(m) => { setFactureOuverteId(null); setTab('missions'); setVue({ type: 'mission', id: m.id }); }}
          onStatus={setFactureStatut} onDownload={dlFacture} />;
      })()}
      {factureForm && <FactureModal form={factureForm} setForm={setFactureForm} clients={clients.items || []}
        showNotification={showNotification} onDone={() => { setFactureForm(null); factures.reload(); }} />}
      {missionForm && <MissionModal form={missionForm} setForm={setMissionForm} clients={clients.items || []} resources={resources}
        showNotification={showNotification} onDone={() => { setMissionForm(null); missions.reload(); }} />}
    </div>
  );
}

function CRMViewToggle({ mode, onChange }) {
      return <div className="crm-view-toggle" role="group" aria-label="Mode d'affichage"><button type="button" className={mode === 'list' ? 'is-active' : ''} onClick={() => onChange('list')} aria-pressed={mode === 'list'}><BI icon="list-ul" />Liste</button><button type="button" className={mode === 'cards' ? 'is-active' : ''} onClick={() => onChange('cards')} aria-pressed={mode === 'cards'}><BI icon="grid-3x3-gap" />Cartes</button></div>;
    }
    function CRMCollectionEmpty({ icon, text }) { return <div className="crm-empty"><BI icon={icon} /><div>{text}</div></div>; }
    function CRMClientCards({ clients, missionsOf, onOpen }) {
      if (!clients.length) return <CRMCollectionEmpty icon="buildings" text="Aucun client — créez la première fiche avec « Nouveau client »." />;
      return <div className="crm-card-grid">{clients.map((c, i) => { const ms = missionsOf(c); const actives = ms.filter(m => m.statut === 'active').length; return <article key={c.id ?? i} className="crm-entity-card crm-clickable-card" onClick={() => onOpen(c)} tabIndex="0" onKeyDown={e => e.key === 'Enter' && onOpen(c)}><div className="crm-card-head"><div className="crm-list-avatar">{initialsOf(c.nom)}</div><BI icon="arrow-up-right" /></div><h3>{c.nom || 'Client'}</h3><p>{[c.secteur, c.ville].filter(Boolean).join(' · ') || 'Fiche client'}</p><div className="crm-card-meta"><Chip kind="mut"><BI icon="person-lines-fill" />{(c.contacts || []).length} contact{(c.contacts || []).length > 1 ? 's' : ''}</Chip><Chip kind={actives ? 'ok' : 'mut'}><BI icon="briefcase" />{actives} active{actives > 1 ? 's' : ''}</Chip></div></article>; })}</div>;
    }
    function CRMMissionCards({ missions, clientName, resName, onOpen }) {
      if (!missions.length) return <CRMCollectionEmpty icon="briefcase" text="Aucune mission — créez-en une avec « Nouvelle mission »." />;
      return <div className="crm-card-grid">{missions.map((m, i) => { const [, sl, sk] = missionChip(m.statut); const rn = resName(m.resource_id); return <article key={m.id ?? i} className="crm-entity-card crm-clickable-card" onClick={() => onOpen(m)} tabIndex="0" onKeyDown={e => e.key === 'Enter' && onOpen(m)}><div className="crm-card-head"><span className="crm-card-kicker">Mission</span><Chip kind={sk}>{sl}</Chip></div><h3>{m.titre || 'Mission'}</h3><p>{clientName(m)}{rn ? ' · ' + rn : ''}</p><div className="crm-card-facts"><span><small>{libelleTarif(m)}</small><strong>{tarifMission(m)}</strong></span><span><small>Période</small><strong>{[fmtDate(m.date_debut), fmtDate(m.date_fin)].filter(Boolean).join(' → ') || '—'}</strong></span>{m.taux_activite != null && m.taux_activite !== '' && <span><small>Activité</small><strong>{m.taux_activite}%</strong></span>}</div></article>; })}</div>;
    }
    function CRMFactureCards({ factures, missions, onOpen, onOpenMission, onStatus, onDownload }) {
      if (!factures.length) return <div className="crm-list-card"><CRMCollectionEmpty icon="receipt" text="Aucune facture — créez la première avec « Nouvelle facture »." /></div>;
      return <div className="crm-card-grid crm-card-grid-invoices">{factures.map((f, i) => { const [, sl, sk] = factureChip(f.statut); const mid = f.mission_id ?? f.missionId ?? null; const mi = mid != null ? missions.find(x => String(x.id) === String(mid)) : null; const ttc = f.total_ttc ?? (Number(f.total_ht || 0) * (1 + Number(f.tva_taux ?? 8.1) / 100)); return <article key={f.id ?? i} className="crm-entity-card" onClick={() => onOpen?.(f)} role="button" tabIndex={0} onKeyDown={(ev) => { if (ev.key === 'Enter') onOpen?.(f); }} style={{ cursor: 'pointer' }}><div className="crm-card-head"><span className="crm-card-kicker">{f.numero || '#' + f.id}</span><Chip kind={sk}>{sl}</Chip></div><h3>{f.client || 'Client non renseigné'}</h3><p>{mi ? <button className="crm-inline-link" onClick={(ev) => { ev.stopPropagation(); onOpenMission(mi); }}>{mi.titre}</button> : 'Mission non rattachée'}{(f.cra_month || f.mois) ? ' · temps ' + (f.cra_month || f.mois) : ''}</p><div className="crm-card-invoice-total"><small>Total TTC</small><strong>{chfAmt(ttc)}</strong></div><div className="crm-card-actions"><ErpiaSelect value={f.statut || 'brouillon'} onChange={e => onStatus(f, e.target.value)} onClick={e => e.stopPropagation()} title="Changer le statut"><option value="brouillon">Brouillon</option><option value="envoyee">Envoyée</option><option value="payee">Payée</option><option value="annulee">Annulée</option></ErpiaSelect><button onClick={(ev) => { ev.stopPropagation(); onDownload(f); }} className="crm-card-secondary"><BI icon="download" />PDF</button></div></article>; })}</div>;
    }
    function CRMResourceCollection({ resources, missions, mode, onSelect, selection, onToggle }) {
      if (!resources.length) return <div className="crm-list-card"><CRMCollectionEmpty icon="person-badge" text="Aucune ressource trouvée." /></div>;
      const resourceName = r => [r?.prenom || r?.first_name, r?.nom || r?.last_name].filter(Boolean).join(' ') || r?.name || r?.email || 'Ressource'; const linked = r => missions.filter(m => String(m.resource_id ?? m.resourceId ?? m.ressource_id ?? '') === String(r.id ?? r.resource_id)).length;
      const coche = (r) => {
        if (!onToggle || r?.id == null) return null;
        const on = selection?.has(String(r.id));
        return (
          <button type="button" className="crm-row-check" data-checked={on ? 'true' : 'false'} aria-pressed={on}
            title={on ? 'Retirer de la sélection' : 'Sélectionner'} aria-label={`${on ? 'Retirer' : 'Sélectionner'} ${resourceName(r)}`}
            onClick={e => { e.stopPropagation(); onToggle(r.id); }}><BI icon="check-lg" /></button>
        );
      };
      return mode === 'cards' ? <div className="crm-card-grid">{resources.map((r, i) => <article key={r.id ?? i} className="crm-entity-card crm-clickable-card" data-sel={selection?.has(String(r.id)) ? 'true' : undefined} onClick={() => onSelect(r)} tabIndex="0" onKeyDown={e => e.key === 'Enter' && onSelect(r)}><div className="crm-card-head">{coche(r)}<div className="crm-list-avatar">{initialsOf(resourceName(r))}</div><BI icon="arrow-up-right" /></div><h3>{resourceName(r)}</h3><p>{r.role || r.poste || r.fonction || r.email || 'Ressource'}</p><div className="crm-card-meta"><Chip kind="mut"><BI icon="briefcase" />{linked(r)} mission{linked(r) > 1 ? 's' : ''}</Chip>{r.statut && <Chip kind="ok">{r.statut}</Chip>}</div></article>)}</div> : <div className="crm-list-card">{resources.map((r, i) => <div key={r.id ?? i} className="crm-list-row crm-clickable-card" data-sel={selection?.has(String(r.id)) ? 'true' : undefined} onClick={() => onSelect(r)} role="button" tabIndex="0" onKeyDown={e => e.key === 'Enter' && onSelect(r)}>{coche(r)}<div className="crm-list-avatar">{initialsOf(resourceName(r))}</div><div style={{ flex: 1, minWidth: 0 }}><strong style={{ color: 'var(--text-strong)', fontSize: '13px' }}>{resourceName(r)}</strong><div style={{ color: 'var(--text-3)', fontSize: '11.5px', marginTop: '2px' }}>{r.role || r.poste || r.fonction || r.email || 'Ressource'}</div></div><Chip kind="mut"><BI icon="briefcase" />{linked(r)} mission{linked(r) > 1 ? 's' : ''}</Chip><BI icon="chevron-right" style={{ color: 'var(--text-faint)' }} /></div>)}</div>;
    }

    function CRMCommandCenter({ organisation, query, loading, results, onQuery, onSelect, onNewFacture, onNewPlacement, onOpenClients, onOpenMissions, onOpenFactures, onOpenResources, onOpenNoyau }) {
  const orgName = organisation?.nom || organisation?.name || organisation?.raison_sociale || organisation?.libelle || 'votre organisation';
  const hasQuery = query.trim().length > 0;
  return (
    <section className="crm-command-center" aria-label="Centre de commande CRM">
      <div className="crm-command-copy">
        <span className="crm-section-kicker">Centre CRM · {orgName}</span>
        <h2>Retrouver et agir depuis un seul endroit</h2>
        <p>Clients, ressources, missions et factures restent toujours à portée de recherche.</p>
      </div>
      <label className="crm-global-search" aria-label="Rechercher dans tout le CRM">
        <BI icon="search" />
        <input value={query} onChange={e => onQuery(e.target.value)} placeholder="Rechercher un client, Ahmed, une mission ou une facture…" />
        {loading && <i className="bi bi-arrow-repeat crm-search-spin" />}
      </label>
      {hasQuery && <div className="crm-global-results" role="listbox">
        {results.length ? results.map(item => <button key={item.type + '-' + item.id} className="crm-global-result" onClick={() => onSelect(item)} role="option">
          <span className={'crm-global-result-icon crm-global-result-' + item.type}><BI icon={item.icon} /></span>
          <span className="crm-global-result-copy"><strong>{item.label}</strong><small>{item.meta || '—'}</small></span>
          <span className="crm-global-result-type">{item.type === 'action' ? 'Action' : item.type === 'client' ? 'Client' : item.type === 'resource' ? 'Ressource' : item.type === 'mission' ? 'Mission' : 'Facture'}</span>
          <BI icon="arrow-up-right" />
        </button>) : <div className="crm-global-empty"><BI icon="search" /><span>Aucun résultat dans les clients, ressources, missions ou factures.</span></div>}
      </div>}
      <div className="crm-command-actions">
        <button className="crm-command-action crm-command-action-primary" onClick={onNewFacture}><BI icon="send" /><span>Envoyer une facture</span></button>
        <button className="crm-command-action" onClick={onNewPlacement}><BI icon="person-check" /><span>Passer un candidat en ressource</span></button>
        <button className="crm-command-action" onClick={onOpenClients}><BI icon="buildings" /><span>Ouvrir les clients</span></button>
        <button className="crm-command-action" onClick={onOpenMissions}><BI icon="briefcase" /><span>Voir les missions</span></button>
        <button className="crm-command-action" onClick={onOpenFactures}><BI icon="receipt" /><span>Suivre les factures</span></button>
        <button className="crm-command-action" onClick={onOpenResources}><BI icon="person-badge" /><span>Voir les ressources</span></button>
        <button className="crm-command-action" onClick={onOpenNoyau}><BI icon="diagram-3" /><span>Voir le Noyau</span></button>
      </div>
    </section>
  );
}

// Le volet du CRM dit l'essentiel — qui, où, sur quelles missions. Le RESTE
// (CRA, contrats, documents, formulaire, informations administratives) vit sur
// la fiche ressource : il manquait juste le chemin pour y aller. On voyait un
// nom, un email, une mission, et c'était un cul-de-sac.
function CRMResourceDrawer({ resource, missions, clients, onClose, onOpenMission, onOpenFiche }) {
  const name = [resource?.prenom || resource?.first_name, resource?.nom || resource?.last_name].filter(Boolean).join(' ') || resource?.name || resource?.email || 'Ressource';
  const resourceId = resource?.id ?? resource?.resource_id;
  // Une ressource naît avant son contrat et avant son formulaire : elle est
  // normalement incomplète. Le serveur dit ce qui manque — on l'affiche, sinon
  // ça se découvre en cherchant pourquoi le contrat sort avec des trous.
  const [manque, setManque] = useState(null);   // [{ cle, libelle }] | null
  useEffect(() => {
    if (resourceId == null) return undefined;
    let annule = false;
    fetch(`${BASE}/resources/${resourceId}`, { headers: authH() })
      .then(r => (r.ok ? r.json() : null))
      .then(j => {
        if (annule || !j) return;
        const d = j.data || j.resource || j;
        const cles = Array.isArray(d.incomplet) ? d.incomplet : null;
        if (!cles) return;   // clé absente : serveur muet, on n'affirme rien
        const libelles = d.incomplet_libelles;
        setManque(cles.map((cle, i) => ({
          cle,
          libelle: (Array.isArray(libelles) ? libelles[i] : libelles?.[cle]) || String(cle).replace(/_/g, ' '),
        })));
      })
      .catch(() => { /* la fiche reste lisible sans ce détail */ });
    return () => { annule = true; };
  }, [resourceId]);
  const linkedMissions = missions.filter(m => String(m.resource_id ?? m.resourceId ?? m.ressource_id ?? '') === String(resourceId));
  const clientFor = (mission) => clients.find(c => String(c.id) === String(mission.client_id))?.nom || mission.client || 'Client';
  return <>
    <div className="crm-resource-overlay" onClick={onClose} />
    <aside className="crm-resource-drawer" role="dialog" aria-label={'Ressource ' + name}>
      <div className="crm-resource-drawer-head"><div><span className="crm-section-kicker">Ressource</span><h2>{name}</h2><p>{resource.role || resource.poste || resource.fonction || 'Profil ressource'}</p></div><button className="crm-drawer-close" onClick={onClose} aria-label="Fermer"><BI icon="x-lg" /></button></div>
      <div className="crm-resource-identity"><span className="crm-resource-avatar">{initialsOf(name)}</span><div><strong>{name}</strong><span>{resource.email || resource.tel || resource.telephone || 'Coordonnées non renseignées'}</span></div></div>
      {Array.isArray(manque) && manque.length > 0 && (
        <div className="crm-resource-manque" role="status">
          <BI icon="exclamation-triangle-fill" />
          <span>
            <strong>{manque.length} information{manque.length > 1 ? 's' : ''} manque{manque.length > 1 ? 'nt' : ''}</strong> sur cette fiche : {manque.map(m => m.libelle).join(', ')}.
            <em>Le formulaire employé les rapporte.</em>
          </span>
        </div>
      )}
      <div className="crm-resource-stat"><strong>{linkedMissions.length}</strong><span>mission{linkedMissions.length > 1 ? 's' : ''} liée{linkedMissions.length > 1 ? 's' : ''}</span></div>
      <div className="crm-resource-section"><span className="crm-section-kicker">Affectations</span>{linkedMissions.length ? linkedMissions.map(m => <button key={m.id} className="crm-resource-mission" onClick={() => onOpenMission(m)}><span><strong>{m.titre || 'Mission'}</strong><small>{clientFor(m)}</small></span><BI icon="arrow-right" /></button>) : <div className="crm-resource-empty">Aucune mission liée à cette ressource.</div>}</div>
      {/* Le bouton NOMME ce qu'on va trouver : « ouvrir la fiche » ne dit pas
          qu'il y a le CRA, les contrats et le formulaire derrière. */}
      {onOpenFiche && resourceId != null && (
        <div className="crm-resource-section">
          <button type="button" className="erp-btn erp-btn--secondary crm-resource-fiche" onClick={() => onOpenFiche(resourceId)}>
            <BI icon="person-vcard" /> Fiche complète — CRA, contrats, documents, formulaire
          </button>
        </div>
      )}
    </aside>
  </>;
}

// ── Nouveau client ───────────────────────────────────────────────────────────
function ClientModal({ form, setForm, showNotification, onDone, onOuvrirExistant }) {
  const [busy, setBusy] = useState(false);
  // UN CLIENT N'EXISTE QU'UNE FOIS (01.10) : le serveur refuse en 409 et nomme
  // l'existant. La fenêtre le dit à côté du nom, et propose d'ouvrir sa fiche
  // — un toast qui disparaît laisserait chercher « où est passé mon client ».
  const [existant, setExistant] = useState(null);   // { id, nom, message }
  const save = async () => {
    if (!form.nom.trim()) { showNotification?.('Le nom du client est requis.', 'error'); return; }
    setBusy(true);
    setExistant(null);
    try {
      const r = await fetch(`${BASE}/clients`, {
        method: 'POST', headers: jsonH(),
        body: JSON.stringify({ nom: form.nom.trim(), secteur: form.secteur.trim() || undefined, ville: form.ville.trim() || undefined }),
      });
      if (r.status === 404 || r.status === 501) { showNotification?.('Les routes CRM arrivent côté serveur — fiche non enregistrée.', 'error'); return; }
      const j = await r.json().catch(() => ({}));
      if (r.status === 409 && j.code === 'client_existe') {
        setExistant({ id: j.existant?.id ?? null, nom: j.existant?.nom || form.nom.trim(), message: j.error });
        return;
      }
      if (!(r.status === 201 || r.ok) || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      showNotification?.(`Client ${form.nom} créé.`, 'success');
      onDone();
    } catch (e) { showNotification?.('Erreur : ' + e.message, 'error'); }
    finally { setBusy(false); }
  };
  return (
    <>
      <div onClick={() => setForm(null)} style={{ position: 'fixed', inset: 0, zIndex: 70, background: 'rgba(8,9,12,0.5)' }} />
      <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', zIndex: 71, width: 'min(460px,94vw)', background: 'var(--panel)', border: '1px solid var(--border)', borderRadius: '14px', boxShadow: '0 24px 60px rgba(8,9,12,0.4)', padding: '20px 22px' }}>
        <div style={{ fontFamily: "'Archivo',sans-serif", fontSize: '18px', fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--text-strong)', marginBottom: '14px' }}>Nouveau client</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <div><div style={{ ...LBL, marginBottom: '5px' }}>Nom *</div><input value={form.nom} onChange={e => { setExistant(null); setForm(p => ({ ...p, nom: e.target.value })); }} style={INPUT} placeholder="UBP" autoFocus
            aria-invalid={existant ? true : undefined} aria-describedby={existant ? 'crm-client-existe' : undefined} /></div>
          {existant && (
            <div id="crm-client-existe" className="crm-client-existe" role="alert">
              <BI icon="exclamation-circle" />
              <span>{existant.message || `Le client « ${existant.nom} » existe déjà.`}</span>
              {existant.id != null && onOuvrirExistant && (
                <button type="button" className="erp-btn erp-btn--secondary erp-btn--sm" onClick={() => onOuvrirExistant(existant)}>
                  <BI icon="buildings" /> Ouvrir la fiche
                </button>
              )}
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            <div><div style={{ ...LBL, marginBottom: '5px' }}>Secteur</div><input value={form.secteur} onChange={e => setForm(p => ({ ...p, secteur: e.target.value }))} style={INPUT} placeholder="Banque privée" /></div>
            <div><div style={{ ...LBL, marginBottom: '5px' }}>Ville</div><input value={form.ville} onChange={e => setForm(p => ({ ...p, ville: e.target.value }))} style={INPUT} placeholder="Genève" /></div>
          </div>
        </div>
        <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <button onClick={() => setForm(null)} className="src-row-hover" style={GHOST_BTN}>Annuler</button>
          <button onClick={save} disabled={busy} style={{ ...PRIMARY_BTN, opacity: busy ? 0.6 : 1 }}><BI icon={busy ? 'arrow-repeat' : 'check-lg'} /> Créer</button>
        </div>
      </div>
    </>
  );
}

// ── Fiche client (drawer) : infos, contacts, missions ────────────────────────
function ClientPage({ client, missions, resName, onRetour, onOuvrirMission, onCreateBesoin, showNotification, onSaved, onSupprime }) {
  const [f, setF] = useState({ nom: client.nom || '', secteur: client.secteur || '', ville: client.ville || '', email_facturation: client.email_facturation || '' });
  // Fiche 360 en UN appel : finances, besoins, factures, interactions.
  // Route absente → le drawer garde sa vue de base et l'annonce une fois.
  const [fiche, setFiche] = useState(null);
  const [ficheAbsente, setFicheAbsente] = useState(false);
  const loadFiche = useCallback(async () => {
    try {
      const r = await fetch(`${BASE}/clients/${client.id}`, { headers: authH() });
      if (r.status === 404 || r.status === 501) { setFicheAbsente(true); return; }
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) return;
      setFiche(j.data || j);
    } catch { /* vue de base conservée */ }
  }, [client.id]);
  useEffect(() => { loadFiche(); }, [loadFiche]);
  const [contacts, setContacts] = useState(Array.isArray(client.contacts) ? client.contacts : []);
  const [busy, setBusy] = useState(false);
  const dirty = f.nom !== (client.nom || '') || f.secteur !== (client.secteur || '') || f.ville !== (client.ville || '') || f.email_facturation !== (client.email_facturation || '');

  // Le vocabulaire des rôles clés se lit AVANT la faute : GET /clients/:id/
  // contacts l'expose avec ses libellés. La même route donne le miroir des
  // contacts (key et role_cle normalisés), qui fait foi sur la ligne de liste.
  // Route absente : on garde les contacts de la liste et les libellés d'usage.
  const [rolesAdmis, setRolesAdmis] = useState(null);
  const [nonPourvus, setNonPourvus] = useState(null);   // rôles clés que personne ne porte
  useEffect(() => {
    let annule = false;
    fetch(`${BASE}/clients/${client.id}/contacts`, { headers: authH() })
      .then(r => (r.ok ? r.json() : null))
      .then(j => {
        if (annule || !j) return;
        const admis = j.roles_admis || j.data?.roles_admis;
        if (Array.isArray(admis) && admis.length) setRolesAdmis(admis);
        // Le serveur dit les rôles vacants — une liste VIDE est une réponse
        // (tout est pourvu), pas une absence : l'écran ne la redéduit pas.
        const vacants = j.non_pourvus || j.data?.non_pourvus;
        if (Array.isArray(vacants)) setNonPourvus(vacants);
        const liste = Array.isArray(j.data) ? j.data : (Array.isArray(j.contacts) ? j.contacts : null);
        if (liste) setContacts(liste);
      })
      .catch(() => { /* la fiche reste utilisable sans ce miroir */ });
    return () => { annule = true; };
  }, [client.id]);
  // Le serveur peut n'admettre qu'un vocabulaire de rôles clés : il le dit
  // aussi en refusant (roles_ignores + roles_admis). Sans cette annonce, un
  // « Reçoit les factures » mal orthographié disparaît en silence et la
  // facture part au mauvais contact.
  const annoncerRoles = (j) => {
    const admis = Array.isArray(j?.roles_admis) ? j.roles_admis : null;
    if (admis?.length) setRolesAdmis(admis);
    const ignores = Array.isArray(j?.roles_ignores) ? j.roles_ignores : [];
    if (!ignores.length) return;
    const lisibles = (admis || []).map(r => r?.libelle || r?.key || '').filter(Boolean);
    const fin = lisibles.length ? ` Rôles reconnus : ${lisibles.join(', ')}.` : '';
    showNotification?.(`Rôle clé non retenu : ${ignores.map(r => r?.libelle || r?.key || r).filter(Boolean).join(', ')}.${fin}`, 'error');
  };

  // Rend le corps de la réponse (objet, donc toujours vrai) pour que l'appelant
  // lise ce que le serveur a réellement enregistré ; false en cas d'échec.
  const put = async (body, okMsg) => {
    setBusy(true);
    try {
      const r = await fetch(`${BASE}/clients/${client.id}`, { method: 'PUT', headers: jsonH(), body: JSON.stringify(body) });
      if (r.status === 404 || r.status === 501) { showNotification?.('Les routes CRM arrivent côté serveur.', 'error'); return false; }
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      showNotification?.(okMsg, 'success');
      onSaved?.();
      return j || {};
    } catch (e) { showNotification?.('Erreur : ' + e.message, 'error'); return false; }
    finally { setBusy(false); }
  };
  // La liste qui fait foi après écriture est celle que le serveur renvoie —
  // clés normalisées comprises — pas notre version optimiste.
  const adopter = (j, secours) => (Array.isArray(j?.contacts_synchronises) ? j.contacts_synchronises : secours);

  const addContact = async (draft) => {
      if (!draft?.nom?.trim()) { showNotification?.('Le nom du contact est requis.', 'error'); return false; }
      const next = [...contacts, {
        nom: draft.nom.trim(), role: draft.role?.trim() || undefined, email: draft.email?.trim() || undefined,
        tel: draft.tel?.trim() || undefined, categorie: draft.categorie?.trim() || 'Autres',
        key: draft.key?.trim() || undefined, photo: draft.photo?.trim() || undefined,
      }];
      const j = await put({ contacts: next }, 'Contact ajouté.');
      if (!j) return false;
      setContacts(adopter(j, next)); annoncerRoles(j); return true;
    };
    const updateContact = async (idx, patch) => {
      const next = contacts.map((contact, i) => i === idx ? { ...contact, ...patch } : contact);
      const j = await put({ contacts: next }, 'Contact mis à jour.');
      if (j) { setContacts(adopter(j, next)); annoncerRoles(j); }
    };
    const removeContact = async (idx) => {
      const next = contacts.filter((_, i) => i !== idx);
      const j = await put({ contacts: next }, 'Contact retiré.');
      if (j) setContacts(adopter(j, next));
    };

  const secTitle = { ...LBL, marginBottom: '9px' };

  // Une fiche en double ou créée par erreur. Le serveur refuse — et dit quoi
  // faire — quand une facture, une mission ou un besoin la retient.
  const supprimerClient = async () => {
    const nbContacts = contacts.length;
    const nbEchanges = Array.isArray(fiche?.interactions) ? fiche.interactions.length : 0;
    const ok = await confirmErpia({
      title: `Supprimer « ${client.nom} » ?`,
      message: nbContacts || nbEchanges
        ? `${nbContacts} contact${nbContacts > 1 ? 's' : ''} et ${nbEchanges} échange${nbEchanges > 1 ? 's' : ''} seront effacés avec la fiche.`
        : 'La fiche sera effacée.',
      detail: 'Refusé si une facture, une mission ou un besoin porte ce client : l\'écran vous dira alors quoi faire.',
      confirmLabel: 'Supprimer le client',
    });
    if (!ok) return;
    setBusy(true);
    try {
      const r = await fetch(`${BASE}/clients/${client.id}`, { method: 'DELETE', headers: authH() });
      const j = await r.json().catch(() => ({}));
      if (r.status === 409) { await alertErpia({ title: 'Ce client est retenu', message: j.error }); return; }
      if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      showNotification?.(j.message || 'Client supprimé.', 'success');
      onSupprime?.();
    } catch (e) { showNotification?.('Erreur : ' + e.message, 'error'); }
    finally { setBusy(false); }
  };

  // Santé du compte : ce que le serveur sait déjà dire. Le dernier échange
  // vient du journal — au-delà de 60 jours, la fiche le signale en ambre.
  const actives = missions.filter(m => m.statut === 'active').length;
  const dernierEchange = (() => {
    const ds = (Array.isArray(fiche?.interactions) ? fiche.interactions : [])
      .map(it => it.date || it.created_at).filter(Boolean)
      .map(d => new Date(d)).filter(d => !Number.isNaN(d.getTime()));
    if (!ds.length) return null;
    return new Date(Math.max(...ds.map(d => d.getTime())));
  })();
  const joursDepuis = dernierEchange ? Math.floor((Date.now() - dernierEchange.getTime()) / 86400000) : null;
  const silencieux = joursDepuis != null && joursDepuis > 60;

  const stat = (libelle, valeur, ton) => (
    <div style={{ minWidth: '112px' }}>
      <div style={{ ...LBL }}>{libelle}</div>
      <div style={{ ...mono, marginTop: '3px', fontSize: '16px', fontWeight: 600, color: ton === 'warn' ? 'var(--warn)' : 'var(--text-strong)' }}>{valeur}</div>
    </div>
  );

  return (
    <div style={{ fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}>
      {/* Fil d'Ariane : on sait toujours d'où l'on vient et où l'on est. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '7px', fontSize: '12.5px', color: 'var(--text-3)', marginBottom: '12px' }}>
        <button onClick={onRetour} className="src-row-hover" style={{ ...GHOST_BTN, padding: '5px 10px', fontSize: '12px' }}><BI icon="chevron-left" /> CRM</button>
        <BI icon="chevron-right" style={{ fontSize: '10px' }} />
        <span style={{ color: 'var(--text-2)', fontWeight: 600 }}>{client.nom}</span>
      </div>

      {/* En-tête : identité + santé du compte */}
      <div style={{ ...CARD, padding: '18px 20px', display: 'flex', alignItems: 'center', gap: '18px', flexWrap: 'wrap' }}>
        <div style={{ width: '52px', height: '52px', borderRadius: '13px', background: 'var(--text-strong)', color: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', ...mono, fontSize: '15px', fontWeight: 600, flex: 'none' }}>{initialsOf(client.nom)}</div>
        <div style={{ flex: 1, minWidth: '180px' }}>
          <div style={{ fontFamily: "'Archivo',sans-serif", fontSize: '23px', fontWeight: 800, letterSpacing: '-0.025em', color: 'var(--text-strong)' }}>{client.nom}</div>
          <div style={{ marginTop: '2px', fontSize: '13px', color: 'var(--text-3)' }}>{[client.secteur, client.ville].filter(Boolean).join(' · ') || 'Fiche client'}</div>
        </div>
        <button onClick={onCreateBesoin} className="crm-client-create-need"><BI icon="plus-lg" />Créer un besoin</button>
        {stat('Missions actives', actives)}
        {fiche?.finances?.encours_ttc != null && stat('En cours TTC', chfAmt(fiche.finances.encours_ttc))}
        {stat('Dernier échange', joursDepuis == null ? '—' : joursDepuis === 0 ? "aujourd'hui" : `il y a ${joursDepuis} j`, silencieux ? 'warn' : undefined)}
      </div>
      {silencieux && (
        <div style={{ marginTop: '10px', padding: '9px 13px', borderRadius: '9px', border: '1px solid rgba(217,119,6,0.35)', background: 'color-mix(in srgb,var(--warn) 7%,transparent)', color: 'var(--text-2)', fontSize: '12.5px' }}>
          <BI icon="exclamation-triangle" style={{ marginRight: '7px', color: 'var(--warn)' }} />
          Aucun échange consigné depuis plus de 60 jours — un point de contact serait utile.
        </div>
      )}

      <ClientOrgChart client={client} contacts={contacts} onAdd={addContact} onUpdate={updateContact} onRemove={removeContact} rolesAdmis={rolesAdmis} nonPourvus={nonPourvus} />

          <div style={{ marginTop: '14px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: '14px', alignItems: 'start' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          {/* Infos */}
          <div style={{ ...CARD, borderRadius: '11px', padding: '14px 16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '9px' }}>
              <span style={{ ...LBL, flex: 1 }}>Informations</span>
              {dirty && <button onClick={() => put({ nom: f.nom.trim(), secteur: f.secteur.trim() || null, ville: f.ville.trim() || null, email_facturation: f.email_facturation.trim() }, 'Fiche mise à jour.')} disabled={busy}
                style={{ ...PRIMARY_BTN, padding: '6px 11px', fontSize: '12px', opacity: busy ? 0.6 : 1 }}><BI icon="check-lg" /> Enregistrer</button>}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '9px' }}>
              <div style={{ gridColumn: '1 / -1' }}><div style={{ fontSize: '11px', color: 'var(--text-3)', marginBottom: '4px' }}>Nom</div><input value={f.nom} onChange={e => setF(p => ({ ...p, nom: e.target.value }))} style={{ ...INPUT, padding: '8px 10px', fontSize: '12.5px' }} /></div>
              <div><div style={{ fontSize: '11px', color: 'var(--text-3)', marginBottom: '4px' }}>Secteur</div><input value={f.secteur} onChange={e => setF(p => ({ ...p, secteur: e.target.value }))} style={{ ...INPUT, padding: '8px 10px', fontSize: '12.5px' }} /></div>
              <div><div style={{ fontSize: '11px', color: 'var(--text-3)', marginBottom: '4px' }}>Ville</div><input value={f.ville} onChange={e => setF(p => ({ ...p, ville: e.target.value }))} style={{ ...INPUT, padding: '8px 10px', fontSize: '12.5px' }} /></div>
              {/* L'adresse à qui partent les factures quand ce n'est pas un
                  contact de l'organigramme (boîte compta). Elle vaut le rôle
                  « Reçoit les factures ». */}
              <div style={{ gridColumn: '1 / -1' }}><div style={{ fontSize: '11px', color: 'var(--text-3)', marginBottom: '4px' }}>Adresse de facturation</div><input value={f.email_facturation} onChange={e => setF(p => ({ ...p, email_facturation: e.target.value }))} type="email" placeholder="factures@client.ch" style={{ ...INPUT, padding: '8px 10px', fontSize: '12.5px' }} /></div>
            </div>
            <div className="crm-fiche-danger">
              <button type="button" onClick={supprimerClient} disabled={busy} className="erp-btn erp-btn--secondary erp-btn--sm"><BI icon="trash" /> Supprimer le client</button>
            </div>
          </div>

          {/* Journal : la mémoire commerciale du compte, en évidence. */}
          {!ficheAbsente && (
            <JournalInteractions clientId={client.id} interactions={fiche?.interactions} showNotification={showNotification} onChanged={loadFiche} />
          )}
        </div>

        {/* Colonne 2 : l'activité — missions, argent, besoins, factures */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          {/* Missions du client */}
          <div style={{ ...CARD, borderRadius: '11px', padding: '14px 16px' }}>
            <div style={secTitle}>Missions</div>
            {missions.length === 0
              ? <div style={{ fontSize: '12.5px', color: 'var(--text-faint)' }}>Aucune mission pour ce client — créez-la dans l'onglet Missions.</div>
              : missions.map((m, i) => {
                const [, sl, sk] = missionChip(m.statut);
                const rn = resName(m.resource_id);
                return (
                  <div key={m.id ?? i} onClick={() => onOuvrirMission?.(m)} className="src-row-hover"
                    style={{ display: 'flex', alignItems: 'center', gap: '9px', padding: '7px 0', borderBottom: '1px solid var(--hair)', fontSize: '12.5px', flexWrap: 'wrap', cursor: 'pointer' }}>
                    <div style={{ flex: 1, minWidth: '130px' }}>
                      <div style={{ fontWeight: 600, color: 'var(--text-strong)' }}>{m.titre}</div>
                      <div style={{ fontSize: '11.5px', color: 'var(--text-3)' }}>{[rn, [fmtDate(m.date_debut), fmtDate(m.date_fin)].filter(Boolean).join(' → ')].filter(Boolean).join(' · ') || '—'}</div>
                    </div>
                    <span style={{ ...mono, fontSize: '12px', color: 'var(--text-2)', flex: 'none' }}>{tarifMission(m)}</span>
                    <Chip kind={sk}>{sl}</Chip>
                    <BI icon="chevron-right" style={{ fontSize: '11px', color: 'var(--text-faint)', flex: 'none' }} />
                  </div>
                );
              })}
          </div>

          {ficheAbsente && (
            <div style={{ ...CARD, borderRadius: '11px', padding: '12px 16px', fontSize: '12px', color: 'var(--text-2)' }}>
              La fiche client complète (finances, besoins, journal) arrive côté serveur.
            </div>
          )}

          {/* Finances : trois chiffres TTC, la vérité du serveur telle quelle */}
          {fiche?.finances && (
            <div style={{ ...CARD, borderRadius: '11px', padding: '14px 16px' }}>
              <div style={secTitle}>Finances</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '10px' }}>
                {[['Facturé TTC', fiche.finances.facture_ttc], ['Payé TTC', fiche.finances.paye_ttc], ['En cours TTC', fiche.finances.encours_ttc]].map(([l, v]) => (
                  <div key={l}>
                    <div style={{ fontSize: '10.5px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-3)' }}>{l}</div>
                    <div style={{ ...mono, marginTop: '3px', fontSize: '15px', fontWeight: 600, color: 'var(--text-strong)' }}>{v != null ? chfAmt(v) : '—'}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Besoins ouverts chez ce client */}
          {Array.isArray(fiche?.besoins) && fiche.besoins.length > 0 && (
            <div style={{ ...CARD, borderRadius: '11px', padding: '14px 16px' }}>
              <div style={secTitle}>Besoins ouverts</div>
              {fiche.besoins.map((b, i) => (
                <div key={b.id ?? i} style={{ display: 'flex', alignItems: 'center', gap: '9px', padding: '6px 0', borderBottom: '1px solid var(--hair)', fontSize: '12.5px' }}>
                  <BI icon="briefcase" style={{ color: 'var(--text-3)', flex: 'none' }} />
                  <span style={{ flex: 1, minWidth: 0, fontWeight: 600, color: 'var(--text-strong)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{b.titre || b.poste || `Besoin ${b.id}`}</span>
                  {b.statut && <Chip kind="mut">{BESOIN_STATUTS[b.statut] || b.statut}</Chip>}
                </div>
              ))}
            </div>
          )}

          {/* Factures du client */}
          {Array.isArray(fiche?.factures) && fiche.factures.length > 0 && (
            <div style={{ ...CARD, borderRadius: '11px', padding: '14px 16px' }}>
              <div style={secTitle}>Factures</div>
              {fiche.factures.map((fa, i) => (
                <div key={fa.id ?? i} style={{ display: 'flex', alignItems: 'center', gap: '9px', padding: '6px 0', borderBottom: '1px solid var(--hair)', fontSize: '12.5px' }}>
                  <span style={{ ...mono, flex: 'none', color: 'var(--text-2)' }}>{fa.numero || fa.number || `#${fa.id}`}</span>
                  <span style={{ flex: 1 }} />
                  <span style={{ ...mono, fontWeight: 600, color: 'var(--text-strong)' }}>{fa.total_ttc != null ? chfAmt(fa.total_ttc) : (fa.montant_ttc != null ? chfAmt(fa.montant_ttc) : '—')}</span>
                  {fa.statut && <Chip kind={fa.statut === 'payee' ? 'ok' : 'mut'}>{FACTURE_STATUTS[fa.statut]?.l || fa.statut}</Chip>}
                </div>
              ))}
            </div>
          )}

        </div>
      </div>
    </div>
  );
}

// ── Fiche mission pleine page ────────────────────────────────────────────────
// Le fil de la mission : besoin d'origine → ressource → factures émises.
// Chaque maillon est cliquable quand le serveur l'expose ; ce qu'il n'expose
// pas encore est annoncé, jamais simulé.
function MissionPage({ mission: m, client, resName, factures, onRetour, onOuvrirClient, onEditer, onOuvrirBesoin, onOuvrirCra, showNotification, onSupprimee }) {
  const [, sl, sk] = missionChip(m.statut);
  const rn = resName(m.resource_id);
  const besoinTitre = m.besoin_titre || m.besoin?.titre || m.besoin?.poste || null;
  const besoinId = m.besoin_id ?? m.besoinId ?? m.besoin?.id ?? null;
  // Les relevés de temps de sa ressource, SUR SA PÉRIODE (servis avec la mission).
  const rel = m.releves || null;
  const releves = rel && (rel.valides || rel.en_attente)
    ? [rel.valides ? `${rel.valides} mois validé${rel.valides > 1 ? 's' : ''}` : null,
       rel.en_attente ? `${rel.en_attente} en attente` : null].filter(Boolean).join(' · ')
    : null;
  const [suppression, setSuppression] = useState(false);
  // Une mission créée par erreur. Facturée, issue d'un placement ou portant
  // des relevés engagés, le serveur refuse et dit de la terminer.
  const supprimer = async () => {
    const ok = await confirmErpia({
      title: `Supprimer « ${m.titre || 'la mission'} » ?`,
      message: 'La mission sera effacée.',
      detail: 'Refusé si elle est facturée, issue d\'un placement ou si des relevés de temps sont engagés : passez-la alors à « terminée ».',
      confirmLabel: 'Supprimer la mission',
    });
    if (!ok) return;
    setSuppression(true);
    try {
      const r = await fetch(`${BASE}/missions/${m.id}`, { method: 'DELETE', headers: authH() });
      const j = await r.json().catch(() => ({}));
      if (r.status === 409) { await alertErpia({ title: 'Cette mission est retenue', message: j.error }); return; }
      if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      showNotification?.(j.message || 'Mission supprimée.', 'success');
      onSupprimee?.();
    } catch (e) { showNotification?.('Erreur : ' + e.message, 'error'); }
    finally { setSuppression(false); }
  };

  const maillon = (icone, libelle, valeur, opts) => (
    <div onClick={opts?.onClick} className={opts?.onClick ? 'src-row-hover' : undefined}
      style={{ ...CARD, borderRadius: '11px', padding: '12px 14px', flex: '1 1 190px', minWidth: 0, cursor: opts?.onClick ? 'pointer' : 'default' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
        <BI icon={icone} style={{ fontSize: '11px', color: 'var(--text-3)' }} />
        <span style={LBL}>{libelle}</span>
        {opts?.onClick && <><span style={{ flex: 1 }} /><BI icon="chevron-right" style={{ fontSize: '10px', color: 'var(--text-faint)' }} /></>}
      </div>
      <div style={{ marginTop: '5px', fontSize: '13.5px', fontWeight: 600, color: valeur ? 'var(--text-strong)' : 'var(--text-faint)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {valeur || opts?.vide || '—'}
      </div>
    </div>
  );

  return (
    <div style={{ fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '7px', fontSize: '12.5px', color: 'var(--text-3)', marginBottom: '12px', flexWrap: 'wrap' }}>
        <button onClick={onRetour} className="src-row-hover" style={{ ...GHOST_BTN, padding: '5px 10px', fontSize: '12px' }}><BI icon="chevron-left" /> CRM</button>
        {client && <>
          <BI icon="chevron-right" style={{ fontSize: '10px' }} />
          <button onClick={onOuvrirClient} className="src-row-hover" style={{ ...GHOST_BTN, padding: '5px 10px', fontSize: '12px' }}>{client.nom}</button>
        </>}
        <BI icon="chevron-right" style={{ fontSize: '10px' }} />
        <span style={{ color: 'var(--text-2)', fontWeight: 600 }}>{m.titre || 'Mission'}</span>
      </div>

      <div style={{ ...CARD, padding: '18px 20px', display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: '200px' }}>
          <div style={{ fontFamily: "'Archivo',sans-serif", fontSize: '23px', fontWeight: 800, letterSpacing: '-0.025em', color: 'var(--text-strong)' }}>{m.titre || 'Mission'}</div>
          <div style={{ marginTop: '2px', fontSize: '13px', color: 'var(--text-3)' }}>
            {[client?.nom || m.client, [fmtDate(m.date_debut), fmtDate(m.date_fin)].filter(Boolean).join(' → ')].filter(Boolean).join(' · ') || '—'}
          </div>
        </div>
        <div><div style={LBL}>{libelleTarif(m)}</div><div style={{ ...mono, marginTop: '3px', fontSize: '16px', fontWeight: 600, color: 'var(--text-strong)' }}>{tarifMission(m)}</div></div>
        {m.taux_activite != null && m.taux_activite !== '' && <div><div style={LBL}>Activité</div><div style={{ ...mono, marginTop: '3px', fontSize: '16px', fontWeight: 600, color: 'var(--text-strong)' }}>{m.taux_activite}%</div></div>}
        <Chip kind={sk}>{sl}</Chip>
        <button onClick={onEditer} className="src-row-hover" style={GHOST_BTN}><BI icon="pencil" /> Modifier</button>
        <button type="button" onClick={supprimer} disabled={suppression} className="erp-btn erp-btn--secondary erp-btn--sm"><BI icon={suppression ? 'arrow-repeat' : 'trash'} /> Supprimer</button>
      </div>

      {/* Le fil : d'où vient la mission, qui la réalise, ce qu'elle a produit */}
      <div style={{ marginTop: '14px' }}>
        <div style={{ ...LBL, marginBottom: '8px' }}>Le fil de la mission</div>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'stretch' }}>
          {maillon('briefcase', 'Besoin d\'origine', besoinTitre || (besoinId != null ? `Besoin ${besoinId}` : null),
            { vide: 'créée hors placement', onClick: besoinId != null && onOuvrirBesoin ? () => onOuvrirBesoin(besoinId) : undefined })}
          {maillon('person-badge', 'Ressource', rn, { vide: 'aucune ressource liée' })}
          {maillon('calendar-check', 'Relevés de temps', releves,
            { vide: m.resource_id ? 'aucun relevé sur sa période' : 'aucune ressource liée', onClick: m.resource_id && onOuvrirCra ? onOuvrirCra : undefined })}
          {maillon('receipt', 'Factures émises', factures.length ? `${factures.length} facture${factures.length > 1 ? 's' : ''}` : null, { vide: 'aucune' })}
        </div>
      </div>

      <div style={{ ...CARD, marginTop: '14px', padding: '14px 16px' }}>
        <div style={{ ...LBL, marginBottom: '9px' }}>Factures de cette mission</div>
        {factures.length === 0 ? (
          <div style={{ fontSize: '12.5px', color: 'var(--text-faint)', lineHeight: 1.6 }}>
            Aucune facture rattachée à cette mission. Les factures créées depuis un relevé de temps
            validé s'y rattacheront automatiquement.
          </div>
        ) : factures.map((f, i) => {
          const [, fl, fk] = factureChip(f.statut);
          return (
            <div key={f.id ?? i} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '7px 0', borderBottom: '1px solid var(--hair)', fontSize: '12.5px', flexWrap: 'wrap' }}>
              <span style={{ ...mono, fontWeight: 700, color: 'var(--text-strong)', flex: 'none' }}>{f.numero || `#${f.id}`}</span>
              {(f.cra_month || f.mois) && <Chip kind="mut" title="Mois de relevé de temps facturé">{f.cra_month || f.mois}</Chip>}
              <span style={{ flex: 1 }} />
              <span style={{ ...mono, fontWeight: 600, color: 'var(--text-strong)' }}>{f.total_ttc != null ? chfAmt(f.total_ttc) : (f.montant_ttc != null ? chfAmt(f.montant_ttc) : '—')}</span>
              <Chip kind={fk}>{fl}</Chip>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Pilotage mensuel des CRA : accès → soumission → validation → facture ──
/**
 * Le mois tel que la ressource l'a saisi — avant de valider quoi que ce soit.
 *
 * GET /resources/:id/cra?month= renvoie { days: { 'YYYY-MM-DD': 1 | 0.5 }, total }.
 * On montre le mois entier : ce qui est travaillé, ce qui ne l'est pas, et les
 * week-ends en retrait. Valider un total sans avoir vu les jours, c'est signer
 * les yeux fermés.
 */
function CRADetailJours({ resourceId, month }) {
  const [etat, setEtat] = useState('chargement');   // chargement | pret | vide | erreur
  const [cra, setCra] = useState(null);
  const [erreur, setErreur] = useState('');
  useEffect(() => {
    let annule = false;
    setEtat('chargement');
    fetch(`${BASE}/resources/${resourceId}/cra?month=${month}`, { headers: authH() })
      .then(async (r) => {
        const j = await r.json().catch(() => ({}));
        if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
        return j.data || j;
      })
      .then((brut) => {
        if (annule) return;
        // La saisie peut arriver sous days, hours ou heures, à la racine ou
        // sous .cra selon le mois. Ne lire que `days` faisait afficher
        // « aucun jour saisi » sur un mois pourtant rempli en heures.
        const d = (brut && brut.cra && typeof brut.cra === 'object') ? { ...brut, ...brut.cra } : (brut || {});
        const jours = (d.days && typeof d.days === 'object') ? d.days : null;
        const heures = ((d.hours || d.heures) && typeof (d.hours || d.heures) === 'object') ? (d.hours || d.heures) : null;
        setCra({ ...d, days: jours, hours: heures, conges: (d.conges && typeof d.conges === 'object') ? d.conges : {}, lieux: (d.lieux && typeof d.lieux === 'object') ? d.lieux : null });
        const rempli = (jours && Object.keys(jours).length) || (heures && Object.keys(heures).length);
        setEtat(rempli ? 'pret' : 'vide');
      })
      .catch((e) => { if (!annule) { setErreur(e.message); setEtat('erreur'); } });
    return () => { annule = true; };
  }, [resourceId, month]);

  const [an, mo] = month.split('-').map(Number);
  const moisLisible = new Date(an, mo - 1, 1).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  // Le mois lu est nommé dans tous les états : sans lui, un écran vide ne dit
  // pas s'il n'y a rien à voir ou si on regarde le mauvais mois.
  const cadre = (contenu, ton) => (
    <div style={{ padding: '10px 2px 2px' }}>
      <div style={{ ...LBL, marginBottom: '7px' }}>Saisie de {moisLisible}</div>
      <div style={{ fontSize: '12.5px', color: ton || 'var(--text-3)' }}>{contenu}</div>
    </div>
  );
  if (etat === 'chargement') return cadre(<><BI icon="arrow-repeat" /> Lecture du CRA…</>, 'var(--text-faint)');
  if (etat === 'erreur') return cadre(erreur, 'var(--danger-fg)');
  if (etat === 'vide') return cadre('Aucun jour saisi pour ce mois.');

  const heures = !!cra.hours;
  const saisie = cra.hours || cra.days || {};
  const nbJours = new Date(an, mo, 0).getDate();
  const jours = [];
  for (let d = 1; d <= nbJours; d += 1) {
    const iso = `${month}-${String(d).padStart(2, '0')}`;
    const valeur = Number(saisie[iso] ?? 0);
    const jour = new Date(an, mo - 1, d).getDay();
    jours.push({ d, iso, valeur, conge: cra.conges[iso] || null, teletravail: valeur > 0 && (cra.lieux || {})[iso] === 'teletravail', weekend: jour === 0 || jour === 6 });
  }
  // LES JOURS DE LA SEMAINE, ALIGNÉS. Une bande de numéros à la file ne se lit
  // pas : « 22 jours saisis » ne dit pas s'il manque des lundis ou si les
  // week-ends ont été travaillés. Le mois est donc posé en 7 colonnes, comme
  // la grille que la ressource remplit dans Mon espace — même semaine, même
  // départ le lundi, mêmes en-têtes. `avant` = les cases vides du 1er.
  const avant = (new Date(an, mo - 1, 1).getDay() + 6) % 7;
  const demi = heures ? 0 : jours.filter(j => j.valeur > 0 && j.valeur < 1).length;
  // Un jour à la fois travaillé et couvert par une absence : c'est ce que
  // l'entreprise doit voir en premier, et faire corriger.
  const conflits = jours.filter(j => j.valeur > 0 && j.conge);
  // Le lieu n'est comptable que si le serveur le porte : clé absente, on ne
  // dit pas « 0 jour en télétravail » sur un mois dont on ignore le lieu.
  const tt = cra.lieux ? jours.filter(j => j.teletravail).length : null;
  const congeLbl = (cg) => (cg?.type === 'maladie' ? 'Maladie' : cg?.type === 'autre' ? 'Absence' : 'Congé');
  const total = Number(cra.total ?? jours.reduce((sum, j) => sum + j.valeur, 0));
  return (
    <div style={{ padding: '10px 2px 2px' }}>
      <div style={{ ...LBL, marginBottom: '7px' }}>Saisie de {moisLisible}</div>
      {/* Les en-têtes et les cases vides sont un REPÈRE VISUEL : chaque jour
          porte déjà son propre libellé (« lundi 7 septembre — 1 jour »), donc
          les répéter à la lecture d'écran n'ajouterait rien. */}
      <div className="crm-cra-mois" aria-label={`Jours saisis en ${moisLisible}`}>
        {JOURS_SEMAINE.map((w, i) => (
          <span key={w} className="crm-cra-entete" aria-hidden="true" data-weekend={i >= 5 ? 'true' : undefined}>{w}</span>
        ))}
        {Array.from({ length: avant }, (_, i) => <span key={`vide${i}`} className="crm-cra-vide" aria-hidden="true" />)}
        {jours.map(j => {
          const dit = `${new Date(an, mo - 1, j.d).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })} — ${j.valeur > 0 ? (heures ? `${j.valeur} h` : `${j.valeur} jour`) : 'non travaillé'}${j.teletravail ? ' · en télétravail' : ''}${j.conge ? ` · ${congeLbl(j.conge)} posé` : ''}`;
          return (
          <span key={j.iso} className="crm-cra-jour" aria-label={dit}
            data-travaille={j.valeur > 0 ? 'true' : undefined}
            data-demi={!heures && j.valeur > 0 && j.valeur < 1 ? 'true' : undefined}
            data-conflit={j.valeur > 0 && j.conge ? 'true' : undefined}
            data-teletravail={j.teletravail ? 'true' : undefined}
            data-conge={j.valeur === 0 && j.conge ? 'true' : undefined}
            data-weekend={j.weekend ? 'true' : undefined}
            title={dit}>
            {j.d}
          </span>
          );
        })}
      </div>
      <div style={{ marginTop: '8px', fontSize: '12px', color: 'var(--text-3)' }}>
        <strong style={{ color: 'var(--text-strong)' }}>{heures ? `${total} heure(s)` : `${total} jour(s)`}</strong> saisi{heures ? 'es' : 's'}{demi ? ` · dont ${demi} demi-journée${demi > 1 ? 's' : ''}` : ''}
        {tt != null && tt > 0 ? ` · dont ${tt} en télétravail` : ''}
        {cra.locked ? ' · verrouillé' : ''}
      </div>
      {conflits.length > 0 && (
        <div style={{ marginTop: '9px', padding: '9px 12px', borderRadius: '9px', border: '1px solid color-mix(in srgb,var(--warn) 45%,transparent)', background: 'color-mix(in srgb,var(--warn) 7%,transparent)', color: 'var(--warn)', fontSize: '11.5px', fontWeight: 600 }}>
          <BI icon="exclamation-triangle-fill" style={{ marginRight: '6px' }} />
          {conflits.length} jour{conflits.length > 1 ? 's' : ''} saisi{conflits.length > 1 ? 's' : ''} alors qu'une absence est posée : {conflits.map(j => `${j.d} (${congeLbl(j.conge).toLowerCase()})`).join(', ')}.
          <span style={{ display: 'block', marginTop: '3px', fontWeight: 400 }}>À faire corriger avant validation.</span>
        </div>
      )}
    </div>
  );
}

function CRMCRA({ showNotification, onFactureCreated }) {
  const monthKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  const shiftMonth = (key, delta) => { const [y, m] = key.split('-').map(Number); return monthKey(new Date(y, m - 1 + delta, 1)); };
  const [month, setMonth] = useState(() => monthKey(new Date()));
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [confirm, setConfirm] = useState(null);
  // Le détail des jours est monté une fois par (ressource, mois) : après une
  // demande de correction ou une réouverture, il affichait encore la saisie
  // d'avant — on validait sur un mois périmé. `tick` change à chaque
  // rechargement et refait lire le détail ouvert.
  const [tick, setTick] = useState(0);
  const load = useCallback(async () => {
    setError('');
    try {
      const r = await fetch(`${BASE}/resources/cra/overview?month=${month}`, { headers: authH() });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      setData(j.data || j);
      setTick(t => t + 1);
    } catch (e) { setData(null); setError(e.message); }
  }, [month]);
  useEffect(() => { load(); }, [load]);

  const changeStatus = async (row, status) => {
    const key = `status-${row.resource_id}`; setBusy(key);
    try {
      const r = await fetch(`${BASE}/resources/${row.resource_id}/cra/status?month=${month}`, { method: 'PUT', headers: jsonH(), body: JSON.stringify({ status }) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      showNotification?.(j.message || (status === 'client_validated' ? 'CRA validé et verrouillé.' : status === 'valide_interne' ? 'CRA validé pour la comptabilité — prêt à facturer.' : 'CRA rouvert pour correction.'), 'success');
      setConfirm(null); await load();
    } catch (e) { showNotification?.('Validation CRA impossible : ' + e.message, 'error'); }
    finally { setBusy(''); }
  };
  const invoice = async (row) => {
    const key = `invoice-${row.resource_id}`; setBusy(key);
    try {
      const r = await fetch(`${BASE}/factures/from-cra`, { method: 'POST', headers: jsonH(), body: JSON.stringify({ resource_id: row.resource_id, month }) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      showNotification?.(`Facture ${j.data?.numero || 'créée'} générée depuis le CRA.`, 'success');
      setConfirm(null); onFactureCreated?.(); await load();
    } catch (e) { showNotification?.('Facturation impossible : ' + e.message, 'error'); }
    finally { setBusy(''); }
  };
  const statusMeta = {
    absent: ['Non saisi', 'mut'], draft: ['En saisie', 'mut'], submitted: ['À valider', 'warn'],
    signature_en_cours: ['Signature client en cours', 'warn'],
    client_validated: ['Validé client', 'ok'], valide_interne: ['Validé — prêt compta', 'ok'],
  };
  const [ouvert, setOuvert] = useState(null);   // resource_id dont on lit le détail

  /**
   * Renvoyer un CRA à sa ressource pour correction.
   *
   * Action SORTANTE : la ressource est prévenue. Le motif est obligatoire —
   * « à corriger » sans dire quoi fait perdre un aller-retour. Le serveur dit
   * s'il a réellement prévenu ; s'il ne l'a pas fait, on le répète plutôt que
   * de laisser croire que le message est parti.
   */
  const [correction, setCorrection] = useState(null); // { row, motif }
  const demanderCorrection = (row) => { setConfirm(null); setCorrection({ row, motif: '' }); };
  const envoyerCorrection = async () => {
    const row = correction?.row;
    const motif = String(correction?.motif || '').trim();
    if (!row || !motif) return;
    const qui = [row.prenom, row.nom].filter(Boolean).join(' ') || 'La ressource';
    const key = `correction-${row.resource_id}`; setBusy(key);
    try {
      const r = await fetch(`${BASE}/resources/${row.resource_id}/cra/correction?month=${month}`, { method: 'POST', headers: jsonH(), body: JSON.stringify({ motif }) });
      const j = await r.json().catch(() => ({}));
      if ([404, 405, 501].includes(r.status) && !j.error && !j.message) {
        showNotification?.('La demande de correction arrive côté serveur — rien n\'a été envoyé.', 'info');
        return;
      }
      if (!r.ok || j.success === false) throw new Error(j.error || j.message || `HTTP ${r.status}`);
      showNotification?.(j.message || 'Correction demandée.', 'success');
      if (j.ressource_prevenue === false) {
        showNotification?.(`${qui} n'a pas été prévenu${qui.endsWith('e') ? 'e' : ''} — transmettez-lui la demande vous-même.`, 'error');
      }
      setCorrection(null);
      load();
    } catch (e) { showNotification?.(e.message, 'error'); }
    finally { setBusy(''); }
  };

  /**
   * Faire signer le CRA au client (signd).
   *
   * Action SORTANTE : un email part chez le client. Elle se confirme, et le
   * message du serveur — qui nomme le contact et l'adresse — est repris tel
   * quel. Route absente : on l'annonce, rien n'est envoyé.
   */
  const demanderSignature = async (row) => {
    const qui = [row.prenom, row.nom].filter(Boolean).join(' ') || 'la ressource';
    const ok = await confirmErpia({
      title: 'Faire signer le CRA au client',
      message: `Le CRA de ${qui} (${Number(row.cra?.total || 0)} jour(s)) part en signature électronique chez le client.`,
      detail: 'Le client reçoit un email signd. Tant qu\'il n\'a pas signé, le CRA reste modifiable depuis « Rouvrir ».',
      confirmLabel: 'Envoyer en signature', tone: 'primary',
    });
    if (!ok) return;
    const key = `signature-${row.resource_id}`; setBusy(key);
    try {
      const r = await fetch(`${BASE}/resources/${row.resource_id}/cra/signature?month=${month}`, { method: 'POST', headers: jsonH(), body: JSON.stringify({}) });
      const j = await r.json().catch(() => ({}));
      if ([404, 405, 501].includes(r.status) && !j.error && !j.message) {
        showNotification?.('La signature électronique du CRA arrive côté serveur — rien n\'a été envoyé.', 'info');
        return;
      }
      if (!r.ok || j.success === false) throw new Error(j.error || j.message || `HTTP ${r.status}`);
      showNotification?.(j.message || 'CRA envoyé au client pour signature.', 'success');
      await load();
    } catch (e) { showNotification?.('Envoi en signature impossible : ' + e.message, 'error'); }
    finally { setBusy(''); }
  };
  const rows = data?.lignes || [];
  const summary = data?.resume || {};
  return (
    <div style={{ display: 'grid', gap: '14px' }}>
      <div style={{ ...CARD, padding: '15px 18px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <div><div style={LBL}>Pilotage CRA</div><div style={{ marginTop: '3px', fontSize: '13px', color: 'var(--text-2)' }}>La ressource saisit, l'équipe valide, puis la facture est générée depuis les jours approuvés.</div></div>
        <div style={{ flex: 1 }} />
        <button onClick={() => setMonth(shiftMonth(month, -1))} style={GHOST_BTN}><BI icon="chevron-left" /></button>
        <input type="month" value={month} onChange={e => setMonth(e.target.value)} style={{ ...INPUT, width: '150px', ...mono }} />
        <button onClick={() => setMonth(shiftMonth(month, 1))} style={GHOST_BTN}><BI icon="chevron-right" /></button>
        <button onClick={load} style={GHOST_BTN}><BI icon="arrow-clockwise" />Actualiser</button>
      </div>
      {data && <div className="crm-kpis">
        <div className="crm-kpi"><div className="crm-kpi-label">Ressources</div><strong className="crm-kpi-value">{summary.total || 0}</strong><span className="crm-kpi-note">missions actives</span></div>
        <div className="crm-kpi"><div className="crm-kpi-label">À relancer</div><strong className="crm-kpi-value">{(summary.absents || 0) + (summary.brouillons || 0)}</strong><span className="crm-kpi-note">non soumis</span></div>
        <div className="crm-kpi crm-kpi-accent"><div className="crm-kpi-label">À valider</div><strong className="crm-kpi-value">{summary.soumis || 0}</strong><span className="crm-kpi-note">notification reçue</span></div>
        {/* Un CRA parti en signature chez le client n'était compté NULLE PART :
            ni « à relancer » (il est soumis), ni « à valider » (personne
            n'attend un clic de nous), ni « validés » (le client n'a pas
            signé). La ressource disparaissait des quatre chiffres alors
            qu'elle était bien dans le total — les colonnes ne totalisaient
            plus, et on cherchait le mois manquant. */}
        {Number(summary.signatures_en_cours || 0) > 0 && (
          <div className="crm-kpi"><div className="crm-kpi-label">En signature</div><strong className="crm-kpi-value">{summary.signatures_en_cours}</strong><span className="crm-kpi-note">chez le client</span></div>
        )}
        {/* « Prêts à facturer » compte AUSSI les mois validés pour la compta :
            ils portent le bouton « Facturer » et n'ont pas encore de facture.
            Ne compter que valides_client faisait BAISSER le chiffre quand on
            avançait dans le circuit. */}
        <div className="crm-kpi"><div className="crm-kpi-label">Validés</div><strong className="crm-kpi-value">{(summary.valides_client || 0) + (summary.prets_compta || 0)}</strong><span className="crm-kpi-note">prêts à facturer</span></div>
        {/* Une incohérence, c'est un jour saisi sur une absence posée. Ça se
            voit avant de valider, pas en ouvrant chaque mois. */}
        {Number(summary.incoherentes || 0) > 0 && (
          <div className="crm-kpi"><div className="crm-kpi-label">Incohérents</div><strong className="crm-kpi-value" style={{ color: 'var(--warn)' }}>{summary.incoherentes}</strong><span className="crm-kpi-note">jour saisi sur une absence</span></div>
        )}
      </div>}
      {error && <div style={{ ...CARD, padding: '16px', color: 'var(--bad-fg)' }}>CRA indisponibles : {error}</div>}
      {!error && !data && <div style={{ ...CARD, padding: '28px', textAlign: 'center', color: 'var(--text-faint)' }}>Chargement des CRA…</div>}
      {data && <div className="crm-list-card">
        {rows.length === 0 ? <div style={{ padding: '36px', textAlign: 'center', color: 'var(--text-faint)' }}>Aucune ressource active pour ce mois.</div> : rows.map(row => {
          const [label, kind] = statusMeta[row.cra?.status] || statusMeta.absent;
          const hasAccess = !!row.access?.user_id;
          return <div key={row.resource_id} className="crm-list-row" style={{ padding: '13px 18px', borderBottom: '1px solid var(--hair)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <div style={{ width: '38px', height: '38px', borderRadius: '10px', background: 'var(--text-strong)', color: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', ...mono, fontSize: '11px', fontWeight: 700 }}>{initialsOf([row.prenom, row.nom].filter(Boolean).join(' '))}</div>
              <div style={{ flex: 1, minWidth: '190px' }}><div style={{ fontWeight: 700, color: 'var(--text-strong)' }}>{[row.prenom, row.nom].filter(Boolean).join(' ') || 'Ressource'}</div><div style={{ fontSize: '12px', color: 'var(--text-3)', marginTop: '2px' }}>{[row.mission_titre, row.client].filter(Boolean).join(' · ') || 'Mission non renseignée'}</div></div>
              <Chip kind={hasAccess ? 'ok' : 'bad'}><BI icon={hasAccess ? 'person-check' : 'person-x'} /> {hasAccess ? 'Accès actif' : 'Accès à créer'}</Chip>
              <Chip kind={kind}>{label}</Chip>
              {Number(row.cra?.incoherences || 0) > 0 && (
                <Chip kind="warn" title="Jours saisis alors qu'une absence est posée"><BI icon="exclamation-triangle-fill" /> {row.cra.incoherences} incohérence{row.cra.incoherences > 1 ? 's' : ''}</Chip>
              )}
              {row.cra?.correction_motif && <Chip kind="warn" title={row.cra.correction_motif}><BI icon="arrow-counterclockwise" /> Correction demandée</Chip>}
              {Number(row.cra?.teletravail || 0) > 0 && (
                <Chip kind="mut" title="Jours travaillés à distance"><BI icon="house" /> {row.cra.teletravail} j en télétravail</Chip>
              )}
              <span style={{ ...mono, minWidth: '54px', textAlign: 'right', fontWeight: 700 }}>{Number(row.cra?.total || 0)} j</span>
              {/* Voir avant de valider : le détail des jours saisis. */}
              {row.cra?.status && row.cra.status !== 'absent' && (
                <button onClick={() => setOuvert(ouvert === row.resource_id ? null : row.resource_id)} style={GHOST_BTN}
                  aria-expanded={ouvert === row.resource_id}>
                  <BI icon={ouvert === row.resource_id ? 'chevron-up' : 'calendar3'} />{ouvert === row.resource_id ? 'Masquer' : 'Voir la saisie'}
                </button>
              )}
              {row.cra?.status === 'submitted' && <>
                <button onClick={() => demanderCorrection(row)} disabled={busy === `correction-${row.resource_id}`} style={GHOST_BTN}>
                  <BI icon="arrow-counterclockwise" />{busy === `correction-${row.resource_id}` ? 'Envoi…' : 'Demander une correction'}
                </button>
                <button onClick={() => demanderSignature(row)} disabled={busy === `signature-${row.resource_id}`} style={GHOST_BTN}>
                  <BI icon="vector-pen" />{busy === `signature-${row.resource_id}` ? 'Envoi…' : 'Faire signer (signd)'}
                </button>
                <button onClick={() => setConfirm({ type: 'validate', row })} style={PRIMARY_BTN}><BI icon="check2-circle" />Valider</button>
              </>}
              {row.cra?.status === 'signature_en_cours' && <>
                <button onClick={() => setConfirm({ type: 'validate', row })} style={GHOST_BTN}><BI icon="check2-circle" />Valider sans attendre</button>
                <button onClick={() => setConfirm({ type: 'reopen', row })} style={GHOST_BTN}>Annuler la demande</button>
              </>}
              {row.cra?.status === 'client_validated' && <>
                <button onClick={() => setConfirm({ type: 'compta', row })} style={PRIMARY_BTN}><BI icon="check2-all" />Valider pour la compta</button>
                <button onClick={() => setConfirm({ type: 'invoice', row })} style={GHOST_BTN}><BI icon="receipt" />Facturer</button>
                <button onClick={() => setConfirm({ type: 'reopen', row })} style={GHOST_BTN}>Rouvrir</button>
              </>}
              {row.cra?.status === 'valide_interne' && <>
                <button onClick={() => setConfirm({ type: 'invoice', row })} style={PRIMARY_BTN}><BI icon="receipt" />Facturer</button>
                <button onClick={() => setConfirm({ type: 'reopen', row })} style={GHOST_BTN}>Rouvrir</button>
              </>}
            </div>
            {row.cra?.correction_motif && (
              <div style={{ marginTop: '9px', padding: '9px 12px', borderRadius: '9px', border: '1px solid color-mix(in srgb,var(--warn) 45%,transparent)', background: 'color-mix(in srgb,var(--warn) 7%,transparent)', color: 'var(--warn)', fontSize: '11.5px' }}>
                <BI icon="arrow-counterclockwise" style={{ marginRight: '6px' }} />
                <strong>Correction demandée :</strong> {row.cra.correction_motif}
              </div>
            )}
            {ouvert === row.resource_id && <CRADetailJours key={`${row.resource_id}-${month}-${tick}`} resourceId={row.resource_id} month={month} />}
            {correction?.row?.resource_id === row.resource_id && (
              <div style={{ marginTop: '10px', padding: '11px 13px', borderRadius: '9px', background: 'var(--code-bg)' }}>
                <div style={{ ...LBL, marginBottom: '6px' }}>Ce qui doit être corrigé</div>
                <textarea value={correction.motif} autoFocus rows={2}
                  onChange={e => setCorrection(c => ({ ...c, motif: e.target.value }))}
                  placeholder="Ex. le 10 est saisi alors qu'une maladie est posée ce jour-là."
                  style={{ ...INPUT, width: '100%', resize: 'vertical', fontSize: '12.5px' }} />
                <div style={{ display: 'flex', alignItems: 'center', gap: '9px', marginTop: '8px' }}>
                  <span style={{ flex: 1, fontSize: '11.5px', color: 'var(--text-3)' }}>Le CRA repassera en saisie et la ressource sera prévenue.</span>
                  <button onClick={() => setCorrection(null)} style={GHOST_BTN}>Annuler</button>
                  <button onClick={envoyerCorrection} disabled={!!busy || !correction.motif.trim()}
                    style={{ ...PRIMARY_BTN, opacity: (busy || !correction.motif.trim()) ? 0.5 : 1 }}>
                    {busy === `correction-${row.resource_id}` ? 'Envoi…' : 'Demander la correction'}
                  </button>
                </div>
              </div>
            )}
            {confirm?.row?.resource_id === row.resource_id && <div style={{ marginTop: '10px', padding: '10px 12px', borderRadius: '9px', background: 'var(--code-bg)', display: 'flex', alignItems: 'center', gap: '9px', flexWrap: 'wrap' }}><span style={{ flex: 1, fontSize: '12.5px', color: 'var(--text-2)' }}>{confirm.type === 'invoice' ? `Créer une facture de ${row.cra.total} jour(s) au TJM de la mission ?` : confirm.type === 'reopen' ? (row.cra?.status === 'signature_en_cours' ? 'Annuler la demande de signature ? Le lien envoyé au client sera invalidé.' : 'Déverrouiller ce CRA pour permettre une correction ?') : confirm.type === 'compta' ? `Valider ${row.cra.total} jour(s) pour la comptabilité ? C'est l'état que reprendra la paie.` : `Valider et verrouiller ${row.cra.total} jour(s) ?`}</span><button onClick={() => setConfirm(null)} style={GHOST_BTN}>Annuler</button><button disabled={!!busy} onClick={() => confirm.type === 'invoice' ? invoice(row) : changeStatus(row, confirm.type === 'reopen' ? 'draft' : confirm.type === 'compta' ? 'valide_interne' : 'client_validated')} style={{ ...PRIMARY_BTN, opacity: busy ? .6 : 1 }}>{busy ? 'Traitement…' : 'Confirmer'}</button></div>}
          </div>;
        })}
      </div>}
    </div>
  );
}

// ── Journal d'interactions du client ─────────────────────────────────────────
// GET/POST /clients/:id/interactions. L'auteur n'est JAMAIS envoyé : le back
// l'attribue à l'utilisateur connecté — le front l'affiche, il ne l'écrit pas.
function JournalInteractions({ clientId, interactions, showNotification, onChanged }) {
  const [liste, setListe] = useState(Array.isArray(interactions) ? interactions : null);
  useEffect(() => { if (Array.isArray(interactions)) setListe(interactions); }, [interactions]);
  const charger = useCallback(async () => {
    try {
      const r = await fetch(`${BASE}/clients/${clientId}/interactions`, { headers: authH() });
      if (!r.ok) return;
      const j = await r.json().catch(() => ({}));
      const arr = j.data || j.interactions || (Array.isArray(j) ? j : []);
      if (Array.isArray(arr)) setListe(arr);
    } catch { /* silencieux : la fiche 360 peut déjà les porter */ }
  }, [clientId]);
  useEffect(() => { if (liste == null) charger(); }, [liste, charger]);

  const [type, setType] = useState('note');
  const [contenu, setContenu] = useState('');
  const [date, setDate] = useState('');
  const [busy, setBusy] = useState(false);
  // Le journal dit qui a dit quoi : un échange se corrige ou se retire par
  // son AUTEUR ou un administrateur — le serveur le vérifie, l'écran ne
  // propose le geste qu'à eux.
  const moi = (() => { try { return APIService.getCurrentUser(); } catch { return null; } })();
  const peutChanger = (it) => moi?.admin === true || (it.created_by != null && Number(it.created_by) === Number(moi?.id));
  const [edition, setEdition] = useState(null); // { id, contenu, type, date }
  const ICONES = { note: 'journal-text', appel: 'telephone', email: 'envelope', rdv: 'calendar-event' };
  const LIBELLES = { note: 'Note', appel: 'Appel', email: 'Email', rdv: 'RDV' };

  const ajouter = async () => {
    if (!contenu.trim()) { showNotification?.('Écrivez le contenu de l\'interaction.', 'error'); return; }
    setBusy(true);
    try {
      const r = await fetch(`${BASE}/clients/${clientId}/interactions`, {
        method: 'POST', headers: jsonH(),
        body: JSON.stringify({ type, contenu: contenu.trim(), ...(date ? { date } : {}) }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      setContenu(''); setDate('');
      showNotification?.('Interaction ajoutée.', 'success');
      await charger(); onChanged?.();
    } catch (e) { showNotification?.('Erreur : ' + e.message, 'error'); }
    finally { setBusy(false); }
  };

  const enregistrerEdition = async () => {
    if (!edition?.contenu?.trim()) { showNotification?.('Un échange ne se vide pas : pour le retirer, utilisez « Retirer ».', 'error'); return; }
    setBusy(true);
    try {
      const r = await fetch(`${BASE}/clients/${clientId}/interactions/${edition.id}`, {
        method: 'PATCH', headers: jsonH(),
        body: JSON.stringify({ contenu: edition.contenu.trim(), type: edition.type, ...(edition.date ? { date: edition.date } : {}) }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      setEdition(null);
      showNotification?.('Échange corrigé.', 'success');
      await charger(); onChanged?.();
    } catch (e) { showNotification?.('Erreur : ' + e.message, 'error'); }
    finally { setBusy(false); }
  };
  const retirer = async (it) => {
    const ok = await confirmErpia({
      title: 'Retirer cet échange du journal ?',
      message: String(it.contenu || '').slice(0, 200),
      confirmLabel: 'Retirer l\'échange',
    });
    if (!ok) return;
    setBusy(true);
    try {
      const r = await fetch(`${BASE}/clients/${clientId}/interactions/${it.id}`, { method: 'DELETE', headers: authH() });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      showNotification?.('Échange retiré du journal.', 'success');
      await charger(); onChanged?.();
    } catch (e) { showNotification?.('Erreur : ' + e.message, 'error'); }
    finally { setBusy(false); }
  };

  return (
    <div style={{ ...CARD, borderRadius: '11px', padding: '14px 16px' }}>
      <div style={{ ...LBL, marginBottom: '9px' }}>Journal d'interactions</div>
      <div style={{ display: 'flex', gap: '7px', flexWrap: 'wrap', marginBottom: '8px' }}>
        <ErpiaSelect value={type} onChange={e => setType(e.target.value)} style={{ ...INPUT, width: 'auto', padding: '8px 10px' }}>
          {Object.keys(LIBELLES).map(k => <option key={k} value={k}>{LIBELLES[k]}</option>)}
        </ErpiaSelect>
        <input type="date" value={date} onChange={e => setDate(e.target.value)} title="Date de l'interaction (vide = aujourd'hui)" style={{ ...INPUT, width: 'auto', padding: '8px 10px' }} />
      </div>
      <textarea value={contenu} onChange={e => setContenu(e.target.value)} rows={2} placeholder="Ce qui s'est dit, ce qui a été convenu…" style={{ ...INPUT, resize: 'vertical', marginBottom: '8px' }} />
      <button onClick={ajouter} disabled={busy} style={{ ...PRIMARY_BTN, padding: '8px 13px', fontSize: '12.5px', opacity: busy ? 0.6 : 1 }}><BI icon="plus-lg" /> Ajouter</button>

      <div style={{ marginTop: '12px' }}>
        {liste == null && <div style={{ fontSize: '12px', color: 'var(--text-faint)' }}>Chargement…</div>}
        {Array.isArray(liste) && liste.length === 0 && <div style={{ fontSize: '12px', color: 'var(--text-faint)' }}>Aucune interaction pour l'instant.</div>}
        {Array.isArray(liste) && liste.map((it, i) => (
          <div key={it.id ?? i} style={{ display: 'flex', gap: '9px', padding: '8px 0', borderTop: i === 0 ? 'none' : '1px solid var(--hair)' }}>
            <BI icon={ICONES[it.type] || 'chat-left-text'} style={{ color: 'var(--text-3)', flex: 'none', marginTop: '2px' }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '11.5px', color: 'var(--text-3)' }}>
                {[fmtDate(it.date || it.created_at), LIBELLES[it.type] || it.type, it.auteur || it.author || it.created_by_name, it.modifie_le ? `corrigé le ${fmtDate(it.modifie_le)}` : null].filter(Boolean).join(' · ')}
              </div>
              {edition?.id === it.id ? (
                <div className="crm-echange-edition">
                  <div style={{ display: 'flex', gap: '7px', flexWrap: 'wrap' }}>
                    <ErpiaSelect value={edition.type} onChange={e => setEdition(p => ({ ...p, type: e.target.value }))} style={{ ...INPUT, width: 'auto', padding: '6px 9px' }}>
                      {Object.keys(LIBELLES).map(k => <option key={k} value={k}>{LIBELLES[k]}</option>)}
                    </ErpiaSelect>
                    <input type="date" value={edition.date} onChange={e => setEdition(p => ({ ...p, date: e.target.value }))} style={{ ...INPUT, width: 'auto', padding: '6px 9px' }} />
                  </div>
                  <textarea value={edition.contenu} onChange={e => setEdition(p => ({ ...p, contenu: e.target.value }))} rows={3} style={{ ...INPUT, resize: 'vertical' }} autoFocus />
                  <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                    <button type="button" onClick={() => setEdition(null)} className="erp-btn erp-btn--secondary erp-btn--sm">Annuler</button>
                    <button type="button" onClick={enregistrerEdition} disabled={busy} className="erp-btn erp-btn--primary erp-btn--sm">Enregistrer la correction</button>
                  </div>
                </div>
              ) : (
                <div style={{ fontSize: '13px', color: 'var(--text)', whiteSpace: 'pre-line' }}>{it.contenu || it.content || ''}</div>
              )}
              {peutChanger(it) && it.id != null && edition?.id !== it.id && (
                <div className="crm-echange-gestes">
                  <button type="button" onClick={() => setEdition({ id: it.id, contenu: it.contenu || '', type: it.type || 'note', date: String(it.date || '').slice(0, 10) })} disabled={busy}>Corriger</button>
                  <button type="button" onClick={() => retirer(it)} disabled={busy}>Retirer</button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Recherche dynamique de client (combobox) ─────────────────────────────────
// Tapez « U » → les clients commençant/contenant « u » s'affichent ; un clic
// sélectionne (client_id posé), une saisie libre reste possible (le nom part
// tel quel, le back le résout ou le crée).
function ClientCombo({ value, clients, onChange }) {
  const [open, setOpen] = useState(false);
  const norm = (s) => String(s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  const q = norm(value);
  const starts = clients.filter(c => norm(c.nom).startsWith(q));
  const contains = clients.filter(c => !norm(c.nom).startsWith(q) && norm(c.nom).includes(q));
  const matches = (q ? [...starts, ...contains] : clients).slice(0, 8);
  const exact = clients.some(c => norm(c.nom) === q);
  return (
    <div style={{ position: 'relative' }}>
      <input value={value} onChange={e => { onChange({ client: e.target.value, client_id: '' }); setOpen(true); }}
        onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)}
        style={INPUT} placeholder="Tapez pour rechercher…" />
      {open && matches.length > 0 && (
        <div style={{ position: 'absolute', left: 0, right: 0, top: '100%', marginTop: '5px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '10px', boxShadow: '0 10px 30px var(--hover-shadow)', zIndex: 80, maxHeight: '224px', overflowY: 'auto' }}>
          {matches.map(c => (
            <button key={c.id} onMouseDown={e => { e.preventDefault(); onChange({ client: c.nom, client_id: c.id }); setOpen(false); }}
              className="src-row-hover"
              style={{ width: '100%', textAlign: 'left', padding: '8px 12px', background: 'transparent', border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: '13px', color: 'var(--text)', display: 'flex', alignItems: 'center', gap: '9px' }}>
              <span style={{ width: '24px', height: '24px', borderRadius: '6px', background: 'var(--text-strong)', color: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', ...mono, fontSize: '10px', fontWeight: 600, flex: 'none' }}>{initialsOf(c.nom)}</span>
              <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 600, color: 'var(--text-strong)' }}>{c.nom}</span>
              {(c.secteur || c.ville) && <span style={{ fontSize: '11px', color: 'var(--text-faint)', flex: 'none' }}>{c.secteur || c.ville}</span>}
            </button>
          ))}
        </div>
      )}
      {value.trim() !== '' && !exact && (
        <div style={{ marginTop: '4px', fontSize: '11px', color: 'var(--text-3)' }}>
          <BI icon="plus-circle" style={{ marginRight: '5px' }} />Saisie libre — « {value.trim()} » sera utilisé tel quel.
        </div>
      )}
    </div>
  );
}

// ── Mission : création / édition ─────────────────────────────────────────────
function MissionModal({ form, setForm, clients, resources, showNotification, onDone }) {
  const [busy, setBusy] = useState(false);
  const isEdit = form.id != null;
  const set = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));

  const save = async () => {
    if (!form.titre.trim()) { showNotification?.('Le titre de la mission est requis.', 'error'); return; }
    const clientSel = clients.find(c => String(c.id) === String(form.client_id));
    if (!clientSel && !String(form.client || '').trim()) { showNotification?.('Choisissez ou saisissez le client.', 'error'); return; }
    setBusy(true);
    try {
      const body = {
        titre: form.titre.trim(),
        client: clientSel ? clientSel.nom : String(form.client).trim(),
        client_id: clientSel ? clientSel.id : undefined,
        resource_id: form.resource_id ? Number(form.resource_id) : null,
        // L'unité et son taux (29.09) : envoyer un « tjm » sur une mission à
        // l'heure était refusé par le serveur — sa modification échouait
        // toujours, avec un message technique.
        unite_facturation: form.unite_facturation === 'heure' ? 'heure' : 'jour',
        taux_facturation: form.tjm !== '' && form.tjm != null ? Number(form.tjm) : null,
        date_debut: form.date_debut || null,
        date_fin: form.date_fin || null,
        taux_activite: form.taux_activite !== '' ? Number(form.taux_activite) : null,
        statut: form.statut || 'draft',
      };
      const r = await fetch(isEdit ? `${BASE}/missions/${form.id}` : `${BASE}/missions`, {
        method: isEdit ? 'PUT' : 'POST', headers: jsonH(), body: JSON.stringify(body),
      });
      if (r.status === 404 || r.status === 501) { showNotification?.('Les routes CRM arrivent côté serveur — mission non enregistrée.', 'error'); return; }
      const j = await r.json().catch(() => ({}));
      if (!(r.status === 201 || r.ok) || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      showNotification?.(isEdit ? 'Mission mise à jour.' : `Mission « ${form.titre} » créée.`, 'success');
      onDone();
    } catch (e) { showNotification?.('Erreur : ' + e.message, 'error'); }
    finally { setBusy(false); }
  };

  const fld = (label, node) => <div><div style={{ ...LBL, marginBottom: '5px' }}>{label}</div>{node}</div>;
  return (
    <>
      <div onClick={() => setForm(null)} style={{ position: 'fixed', inset: 0, zIndex: 70, background: 'rgba(8,9,12,0.5)' }} />
      <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', zIndex: 71, width: 'min(560px,94vw)', maxHeight: '88vh', overflowY: 'auto', background: 'var(--panel)', border: '1px solid var(--border)', borderRadius: '14px', boxShadow: '0 24px 60px rgba(8,9,12,0.4)', padding: '20px 22px' }}>
        <div style={{ fontFamily: "'Archivo',sans-serif", fontSize: '18px', fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--text-strong)', marginBottom: '14px' }}>{isEdit ? 'Mission' : 'Nouvelle mission'}</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '11px' }}>
          <div style={{ gridColumn: '1 / -1' }}>{fld('Titre *', <input value={form.titre} onChange={set('titre')} style={INPUT} placeholder="Data Engineer — équipe Core Banking" autoFocus />)}</div>
          {fld('Client *', <ClientCombo value={form.client || ''} clients={clients}
            onChange={({ client, client_id }) => setForm(p => ({ ...p, client, client_id }))} />)}
          {fld('Ressource liée', <ErpiaSelect value={form.resource_id} onChange={e => setForm(p => ({ ...p, resource_id: e.target.value }))} style={INPUT}>
            <option value="">— aucune —</option>
            {resources.map(rr => <option key={rr.id} value={rr.id}>{[rr.prenom, rr.nom].filter(Boolean).join(' ')}</option>)}
          </ErpiaSelect>)}
          {fld('Facturation', (
            <div style={{ display: 'inline-flex', gap: '4px', padding: '3px', borderRadius: '9px', background: 'var(--code-bg)' }}>
              {[['jour', 'Au jour'], ['heure', 'À l\'heure']].map(([k, l]) => (
                <button key={k} type="button" onClick={() => setForm(p => ({ ...p, unite_facturation: k }))}
                  style={{ padding: '6px 12px', borderRadius: '7px', border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: '12.5px', fontWeight: 600, background: (form.unite_facturation || 'jour') === k ? 'var(--accent)' : 'transparent', color: (form.unite_facturation || 'jour') === k ? 'var(--accent-fg)' : 'var(--text-2)' }}>{l}</button>
              ))}
            </div>
          ))}
          {fld(form.unite_facturation === 'heure' ? 'Taux horaire (CHF/heure)' : 'TJM (CHF/jour)', <input value={form.tjm} onChange={set('tjm')} type="number" min="0" style={{ ...INPUT, ...mono }} placeholder={form.unite_facturation === 'heure' ? '140' : '950'} />)}
          {fld('Taux d\'activité (%)', <input value={form.taux_activite} onChange={set('taux_activite')} type="number" min="10" max="100" style={{ ...INPUT, ...mono }} placeholder="100" />)}
          {fld('Début', <input value={form.date_debut} onChange={set('date_debut')} type="date" style={INPUT} />)}
          {fld('Fin', <input value={form.date_fin} onChange={set('date_fin')} type="date" style={INPUT} />)}
          <div style={{ gridColumn: '1 / -1' }}>{fld('Statut', (
            <div style={{ display: 'inline-flex', gap: '4px', padding: '3px', borderRadius: '9px', background: 'var(--code-bg)' }}>
              {MISSION_STATUTS.map(([k, l]) => (
                <button key={k} onClick={() => setForm(p => ({ ...p, statut: k }))}
                  style={{ padding: '6px 12px', borderRadius: '7px', border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: '12.5px', fontWeight: 600, background: form.statut === k ? 'var(--accent)' : 'transparent', color: form.statut === k ? 'var(--accent-fg)' : 'var(--text-2)' }}>{l}</button>
              ))}
            </div>
          ))}
            <div style={{ marginTop: '6px', fontSize: '11.5px', color: 'var(--text-3)' }}>Ce statut décrit le cycle de la mission. Le CRA possède un statut séparé dans l’onglet CRA.</div>
          </div>
        </div>
        <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <button onClick={() => setForm(null)} className="src-row-hover" style={GHOST_BTN}>Annuler</button>
          <button onClick={save} disabled={busy} style={{ ...PRIMARY_BTN, opacity: busy ? 0.6 : 1 }}>
            <BI icon={busy ? 'arrow-repeat' : 'check-lg'} /> {isEdit ? 'Enregistrer' : 'Créer la mission'}
          </button>
        </div>
      </div>
    </>
  );
}

// ── Paramètres du CRM ───────────────────────────────────────────────────────
// Tout ce qui gouverne la facturation se règle ici, à côté des factures — plus
// dans la Configuration générale, où personne n'allait le chercher.
// L'écran répond à UNE question : sur quel compte le client paie. D'où la
// chaîne d'encaissement en tête, qui montre laquelle des deux cartes ci-dessous
// est réellement appliquée aux nouvelles factures.
function CRMParametrage({ showNotification }) {
  const [facturiers, setFacturiers] = useState(null);   // null = pas encore lu
  const majListe = useCallback((arr) => setFacturiers(arr), []);
  const actif = (facturiers || []).find(f => f.actif) || null;
  return (
    <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
      <ChaineEncaissement facturier={actif} charge={facturiers !== null} />
      <FacturationCoordsCard showNotification={showNotification} />
      <div style={{ ...CARD, padding: '14px 18px' }}>
        <div style={{ ...LBL, marginBottom: '11px' }}>Affacturage — qui encaisse à votre place</div>
        <CRMFacturiers showNotification={showNotification} onListe={majListe} />
      </div>
    </div>
  );
}

// La chaîne d'encaissement : deux maillons, un seul allumé. C'est la seule
// audace de l'écran — elle rend visible une règle qui, sinon, ne se découvre
// qu'en ouvrant une facture partie sur le mauvais compte.
function ChaineEncaissement({ facturier, charge }) {
  const maillon = (titre, detail, on) => (
    <div className="crm-chaine-maillon" data-on={on ? 'oui' : 'non'}>
      <span className="crm-chaine-titre">{titre}</span>
      <span className="crm-chaine-detail">{detail}</span>
    </div>
  );
  return (
    <div className="crm-chaine" role="group" aria-label="Compte sur lequel le client paie">
      <span style={{ ...LBL, marginBottom: 0 }}>Le client paie sur</span>
      <div className="crm-chaine-rail">
        {maillon('Vos coordonnées', 'Par défaut', !facturier)}
        <BI icon="arrow-right" style={{ fontSize: '11px', color: 'var(--text-faint)' }} />
        {maillon(facturier ? facturier.nom : 'Un facturier', charge && !facturier ? 'Aucun facturier actif' : 'Affacturage', !!facturier)}
      </div>
    </div>
  );
}

// ── Rappel sur l'écran Factures ─────────────────────────────────────────────
// Le pavé de saisie a quitté la liste des factures : il reste ce rappel, qui ne
// s'affiche que si l'IBAN manque vraiment, et qui mène là où on le corrige.
function FacturationRappel({ onOuvrir }) {
  const [manque, setManque] = useState(false);
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${BASE}/config/facturation`, { headers: authH() });
        if (!r.ok) return;   // route absente : rien à reprocher à l'utilisateur
        const j = await r.json().catch(() => ({}));
        const raw = j.data ?? j;
        const d = raw.value ?? raw;
        setManque(!String(d.iban || '').trim());
      } catch { /* réseau : on se tait plutôt que d'alarmer à tort */ }
    })();
  }, []);
  if (!manque) return null;
  return (
    <div style={{ marginTop: '14px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', padding: '9px 13px', borderRadius: '9px', border: '1px solid color-mix(in srgb,var(--warn) 45%,transparent)', background: 'color-mix(in srgb,var(--warn) 7%,transparent)', color: 'var(--warn)', fontSize: '11.5px', fontWeight: 600 }}>
      <BI icon="exclamation-triangle-fill" />
      <span style={{ flex: 1 }}>Sans IBAN, le bloc de paiement n'apparaît pas sur vos factures.</span>
      <button onClick={onOuvrir} className="erp-btn erp-btn--secondary erp-btn--sm">Régler dans Paramètres</button>
    </div>
  );
}

// ── Coordonnées de facturation (org) : imprimées sur chaque facture ──────────
// GET/PUT /config/facturation → { iban, bic?, adresse?, npa_ville?, tva_numero? }
// (même pattern que les autres clés config : PUT { value: {…} }).
function FacturationCoordsCard({ showNotification }) {
  const KEYS = ['iban', 'bic', 'adresse', 'npa_ville', 'tva_numero', 'site_web'];
  const [f, setF] = useState(null);   // null = chargement
  const [orig, setOrig] = useState({});
  const [missing, setMissing] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${BASE}/config/facturation`, { headers: authH() });
        if (r.status === 404 || r.status === 501) { setMissing(true); setF(Object.fromEntries(KEYS.map(k => [k, '']))); return; }
        const j = await r.json().catch(() => ({}));
        // La valeur est renvoyée sous .value (comme le PUT) — tolère aussi
        // .data ou la racine selon la forme du back.
        const raw = j.data ?? j;
        const d = raw.value ?? raw;
        const v = Object.fromEntries(KEYS.map(k => [k, d[k] || '']));
        setF(v); setOrig(v);
      } catch { setF(Object.fromEntries(KEYS.map(k => [k, '']))); }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const dirty = f && KEYS.some(k => f[k] !== (orig[k] || ''));
  // Le refus du serveur s'accroche AU CHAMP fautif — c'est là qu'on corrige,
  // pas dans une notification qui s'évapore.
  const [champErreur, setChampErreur] = useState(null); // { champ, message }
  const ibanRef = React.useRef(null);
  const save = async () => {
    setBusy(true);
    setChampErreur(null);
    try {
      // Le serveur fusionne : on envoie l'ensemble, un champ vidé reste vidé.
      const r = await fetch(`${BASE}/config/facturation`, { method: 'PUT', headers: jsonH(), body: JSON.stringify({ value: f }) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) {
        const msg = j.error || j.message || `HTTP ${r.status}`;
        if (r.status === 400) {
          const champ = /iban/i.test(msg) ? 'iban' : (KEYS.find(k => new RegExp(k.replace('_', '.?'), 'i').test(msg)) || 'iban');
          setChampErreur({ champ, message: msg });
          if (champ === 'iban') ibanRef.current?.focus();
          return;
        }
        throw new Error(msg);
      }
      setOrig({ ...f });
      showNotification?.('Coordonnées de facturation enregistrées — elles figureront sur les prochains PDF.', 'success');
    } catch (e) { showNotification?.('Erreur : ' + e.message, 'error'); }
    finally { setBusy(false); }
  };
  const fld = (label, k, props = {}) => (
    <div>
      <div style={{ fontSize: '11px', color: 'var(--text-3)', marginBottom: '4px' }}>{label}</div>
      <input ref={k === 'iban' ? ibanRef : undefined} value={f?.[k] || ''}
        onChange={e => { const v = e.target.value; setF(p => ({ ...p, [k]: v })); if (champErreur?.champ === k) setChampErreur(null); }}
        aria-invalid={champErreur?.champ === k || undefined}
        style={{ ...INPUT, padding: '8px 10px', fontSize: '12.5px', ...(props.mono ? mono : {}), ...(champErreur?.champ === k ? { borderColor: 'var(--danger-bd)' } : null) }}
        placeholder={props.placeholder || ''} />
      {champErreur?.champ === k && (
        <span role="alert" style={{ display: 'block', marginTop: '4px', fontSize: '11px', color: 'var(--danger-fg)' }}>{champErreur.message}</span>
      )}
    </div>
  );
  return (
    <div style={{ ...CARD, padding: '14px 18px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
        <span style={{ ...LBL, flex: 1 }}>Coordonnées de facturation — imprimées sur chaque facture</span>
        {dirty && <button onClick={save} disabled={busy} style={{ ...PRIMARY_BTN, padding: '6px 11px', fontSize: '12px', opacity: busy ? 0.6 : 1 }}><BI icon="check-lg" /> Enregistrer</button>}
      </div>
      {f === null ? <div style={{ fontSize: '12px', color: 'var(--text-faint)' }}>Chargement…</div> : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '9px' }}>
            {fld('IBAN', 'iban', { placeholder: 'CH93 0076 2011 6238 5295 7', mono: true })}
            {fld('BIC / SWIFT', 'bic', { placeholder: 'UBSWCHZH80A', mono: true })}
            {fld('Adresse', 'adresse', { placeholder: 'Rue du Rhône 14' })}
            {fld('NPA / Ville', 'npa_ville', { placeholder: '1204 Genève' })}
            {fld('N° TVA (IDE)', 'tva_numero', { placeholder: 'CHE-123.456.789 TVA', mono: true })}
            {fld('Site web (pied de la carte profil)', 'site_web', { placeholder: 'www.votresociete.ch' })}
          </div>
          {!missing && !String(f.iban || '').trim() && (
            <div style={{ marginTop: '8px', padding: '8px 12px', borderRadius: '8px', border: '1px solid color-mix(in srgb,var(--warn) 45%,transparent)', background: 'color-mix(in srgb,var(--warn) 7%,transparent)', color: 'var(--warn)', fontSize: '11.5px', fontWeight: 600 }}>
              <BI icon="exclamation-triangle-fill" style={{ marginRight: '6px' }} />Sans IBAN, le bloc de paiement n'apparaît pas sur vos factures — renseignez-le avant d'envoyer la première.
            </div>
          )}
          {missing && <div style={{ marginTop: '8px', fontSize: '11.5px', color: 'var(--text-3)' }}><BI icon="hourglass-split" style={{ marginRight: '5px' }} />La clé config /config/facturation arrive côté serveur — vos saisies aboutiront dès sa mise en ligne.</div>}
        </>
      )}
    </div>
  );
}

// ── Nouvelle facture (mode free) : lignes libres + TVA + échéance ────────────
function FactureModal({ form, setForm, clients, showNotification, onDone }) {
  const [busy, setBusy] = useState(false);
  const [ibanOk, setIbanOk] = useState(null); // null = inconnu, true/false = IBAN configuré ?
  const [customMention, setCustomMention] = useState(false); // motif TVA saisi à la main
  // Qui encaisse. En affacturage, le client paie le facturier : c'est SON IBAN
  // et SA mention de cession qui doivent partir sur le PDF. Le facturier par
  // défaut est donc posé sur la facture dès sa création — sans quoi elle naît
  // avec nos coordonnées et le paiement n'est pas libératoire.
  const [facturiers, setFacturiers] = useState([]);
  const [facturierId, setFacturierId] = useState(null); // null = pas encore choisi
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${BASE}/config/facturation`, { headers: authH() });
        if (!r.ok) { setIbanOk(true); return; } // route absente : ne pas bloquer
        const j = await r.json().catch(() => ({}));
        const raw = j.data ?? j;
        const d = raw.value ?? raw;
        setIbanOk(!!String(d.iban || '').trim());
      } catch { setIbanOk(true); }
    })();
    (async () => {
      try {
        const r = await fetch(`${BASE}/config/facturiers`, { headers: authH() });
        if (!r.ok) { setFacturierId(''); return; }
        const j = await r.json().catch(() => ({}));
        const a = j.data || j.facturiers || (Array.isArray(j) ? j : []);
        const liste = Array.isArray(a) ? a : [];
        setFacturiers(liste);
        const actif = liste.find(x => x.actif);
        setFacturierId(actif ? String(actif.id) : '');
      } catch { setFacturierId(''); }
    })();
  }, []);
  const facturierChoisi = facturiers.find(x => String(x.id) === String(facturierId)) || null;
  const lignes = form.lignes || [];
  const setLigne = (i, k, v) => setForm(p => ({ ...p, lignes: p.lignes.map((l, j) => (j === i ? { ...l, [k]: v } : l)) }));
  const totalHT = lignes.reduce((sum, l) => sum + (Number(l.quantite) || 0) * (Number(l.prix_unitaire) || 0), 0);
  const tvaMontant = totalHT * ((Number(form.tva_taux) || 0) / 100);
  const totalTTC = totalHT + tvaMontant;

  const save = async () => {
    const clientSel = clients.find(c => String(c.id) === String(form.client_id));
    if (!clientSel && !String(form.client || '').trim()) { showNotification?.('Choisissez ou saisissez le client.', 'error'); return; }
    const lignesOk = lignes.filter(l => String(l.description || '').trim() && Number(l.prix_unitaire) > 0);
    if (!lignesOk.length) { showNotification?.('Ajoutez au moins une ligne (description + prix).', 'error'); return; }
    setBusy(true);
    try {
      const r = await fetch(`${BASE}/factures`, {
        method: 'POST', headers: jsonH(),
        body: JSON.stringify({
          client: clientSel ? clientSel.nom : String(form.client).trim(),
          client_id: clientSel ? clientSel.id : undefined,
          lignes: lignesOk.map(l => ({ description: l.description.trim(), quantite: Number(l.quantite) || 1, prix_unitaire: Number(l.prix_unitaire) })),
          client_adresse: form.client_adresse?.trim() || undefined,
          tva_taux: (form.tva_taux === '' || form.tva_taux == null) ? 8.1 : Number(form.tva_taux),
          ...(Number(form.tva_taux) === 0 && form.tva_mention?.trim() ? { tva_mention: form.tva_mention.trim() } : {}),
          echeance: form.echeance || undefined,
          note: form.note?.trim() || undefined,
          ...(facturierId ? { facturier_id: Number(facturierId) || facturierId } : {}),
        }),
      });
      if (r.status === 404 || r.status === 501) { showNotification?.('Les routes /factures arrivent côté serveur — facture non enregistrée.', 'error'); return; }
      const j = await r.json().catch(() => ({}));
      if (!(r.status === 201 || r.ok) || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      const d = j.data || j;
      // Totaux : TOUJOURS ceux du serveur (les calculs front ne font pas foi)
      showNotification?.(`Facture ${d.numero || ''} créée — ${chfAmt(d.total_ttc ?? totalTTC)} TTC.`.replace('  ', ' '), 'success');
      // Preuve avant silence : si on a demandé un facturier et que la facture
      // revient sans, le PDF partira avec NOS coordonnées. On le dit, plutôt
      // que de le découvrir en ouvrant le fichier.
      if (facturierId) {
        const pose = d.facturier_id ?? d.facturier?.id ?? null;
        if (pose == null) {
          showNotification?.(`${facturierChoisi?.nom || 'Le facturier'} n'a pas été posé sur la facture — le PDF portera vos coordonnées bancaires. Ouvrez la facture pour le corriger.`, 'error');
        }
      }
      onDone();
    } catch (e) { showNotification?.('Erreur : ' + e.message, 'error'); }
    finally { setBusy(false); }
  };

  return (
    <>
      <div onClick={() => setForm(null)} style={{ position: 'fixed', inset: 0, zIndex: 70, background: 'rgba(8,9,12,0.5)' }} />
      <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', zIndex: 71, width: 'min(640px,94vw)', maxHeight: '88vh', overflowY: 'auto', background: 'var(--panel)', border: '1px solid var(--border)', borderRadius: '14px', boxShadow: '0 24px 60px rgba(8,9,12,0.4)', padding: '20px 22px' }}>
        <div style={{ fontFamily: "'Archivo',sans-serif", fontSize: '18px', fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--text-strong)', marginBottom: '4px' }}>Nouvelle facture</div>
        <div style={{ fontSize: '12px', color: 'var(--text-3)', marginBottom: '14px' }}>
          {facturierChoisi
            ? <>Facture libre — le PDF portera l'IBAN de {facturierChoisi.nom} et sa mention de cession.</>
            : <>Facture libre — le PDF portera vos coordonnées bancaires (IBAN) et l'adresse du client.</>}
        </div>

        {/* Nos coordonnées ne servent que si personne n'encaisse à notre place :
            avec un facturier, l'alerte IBAN n'a pas lieu d'être. */}
        {ibanOk === false && !facturierChoisi && (
          <div style={{ marginBottom: '14px', padding: '11px 14px', borderRadius: '10px', border: '1px solid var(--danger-bd)', background: 'var(--danger-bg)', color: 'var(--danger-fg)', fontSize: '12.5px', display: 'flex', alignItems: 'flex-start', gap: '9px' }}>
            <BI icon="exclamation-triangle-fill" style={{ marginTop: '1px', flex: 'none' }} />
            <div style={{ flex: 1 }}>
              <b>Aucun IBAN configuré — le client ne saura pas où payer.</b> Le PDF n'affichera pas de coordonnées de paiement.
              <button onClick={() => setForm(null)} style={{ display: 'block', marginTop: '6px', padding: '5px 11px', borderRadius: '7px', border: 'none', background: 'var(--danger-fg)', color: 'var(--accent-fg)', fontFamily: 'inherit', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>
                Fermer et renseigner l'IBAN (carte « Coordonnées de facturation » en haut)
              </button>
            </div>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 130px 110px', gap: '11px' }}>
          <div>
            <div style={{ ...LBL, marginBottom: '5px' }}>Client *</div>
            <ClientCombo value={form.client || ''} clients={clients}
              onChange={({ client, client_id }) => setForm(p => ({ ...p, client, client_id }))} />
          </div>
          <div>
            <div style={{ ...LBL, marginBottom: '5px' }}>Échéance</div>
            <input type="date" value={form.echeance} onChange={e => setForm(p => ({ ...p, echeance: e.target.value }))} style={INPUT} />
          </div>
          <div>
            <div style={{ ...LBL, marginBottom: '5px' }}>TVA (%)</div>
            <input type="number" step="0.1" min="0" value={form.tva_taux} onChange={e => setForm(p => ({ ...p, tva_taux: e.target.value }))} style={{ ...INPUT, ...mono }} />
          </div>
        </div>

        {facturiers.length > 0 && (
          <div style={{ marginTop: '11px' }}>
            <div style={{ ...LBL, marginBottom: '5px' }}>Encaissement</div>
            <ErpiaSelect value={facturierId ?? ''} onChange={e => setFacturierId(e.target.value)} style={INPUT}>
              <option value="">Vos coordonnées bancaires</option>
              {facturiers.map(x => <option key={x.id} value={x.id}>{x.nom}{x.actif ? ' (par défaut)' : ''}</option>)}
            </ErpiaSelect>
            {facturierChoisi && (
              <div style={{ marginTop: '6px', display: 'flex', alignItems: 'flex-start', gap: '8px', fontSize: '11.5px', color: 'var(--text-3)' }}>
                <BI icon="bank" style={{ flex: 'none', marginTop: '2px' }} />
                <span>
                  Le client règle sur le compte de {facturierChoisi.nom}.
                  {facturierChoisi.iban && <span style={{ ...mono, display: 'block', marginTop: '2px' }}>{String(facturierChoisi.iban).replace(/\s+/g, '').replace(/(.{4})/g, '$1 ').trim()}</span>}
                </span>
              </div>
            )}
          </div>
        )}

        <div style={{ marginTop: '11px' }}>
          <div style={{ ...LBL, marginBottom: '5px' }}>Adresse du client (sur le PDF)</div>
          <input value={form.client_adresse || ''} onChange={e => setForm(p => ({ ...p, client_adresse: e.target.value }))}
            placeholder="Rue, NPA ville — laissée vide : la ville de la fiche CRM est utilisée" style={{ ...INPUT, fontSize: '12.5px' }} />
        </div>

        {/* Taux courants — la saisie libre reste maîtresse (taux custom OK) */}
        <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap', marginTop: '8px' }}>
          {[['8.1', 'CH 8.1 %'], ['2.6', 'CH réduit 2.6 %'], ['3.8', 'CH hébergement 3.8 %'], ['20', 'FR 20 %'], ['0', 'Exonérée 0 %']].map(([v, l]) => {
            const on = String(form.tva_taux) === v;
            return (
              <button key={v} onClick={() => setForm(p => ({ ...p, tva_taux: v }))}
                style={{ padding: '4px 10px', borderRadius: '99px', border: on ? 'none' : '1px solid var(--input-border)', background: on ? 'var(--accent)' : 'var(--ghost-bg)', color: on ? 'var(--accent-fg)' : 'var(--text-2)', fontFamily: 'inherit', fontSize: '11.5px', fontWeight: 600, cursor: 'pointer' }}>
                {l}
              </button>
            );
          })}
        </div>
        {String(form.tva_taux) === '0' && (
          <div style={{ marginTop: '9px' }}>
            <div style={{ ...LBL, marginBottom: '5px' }}>Motif d'exonération (imprimé à la place de la TVA)</div>
            <ErpiaSelect value={customMention ? '__custom' : (TVA_MENTIONS.includes(form.tva_mention) ? form.tva_mention : '')}
              onChange={e => { const v = e.target.value; if (v === '__custom') { setCustomMention(true); setForm(p => ({ ...p, tva_mention: '' })); } else { setCustomMention(false); setForm(p => ({ ...p, tva_mention: v })); } }}
              style={{ ...INPUT, fontSize: '12.5px' }}>
              <option value="">Défaut — « TVA non applicable — prestation exonérée »</option>
              {TVA_MENTIONS.map(m => <option key={m} value={m}>{m}</option>)}
              <option value="__custom">Autre motif (saisie libre)…</option>
            </ErpiaSelect>
            {customMention && (
              <input value={form.tva_mention || ''} onChange={e => setForm(p => ({ ...p, tva_mention: e.target.value }))}
                placeholder="Votre mention d'exonération" style={{ ...INPUT, fontSize: '12.5px', marginTop: '6px' }} autoFocus />
            )}
          </div>
        )}

        <div style={{ ...LBL, margin: '16px 0 6px' }}>Lignes</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
          {lignes.map((l, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 70px 110px 110px 30px', gap: '7px', alignItems: 'center' }}>
              <input value={l.description} onChange={e => setLigne(i, 'description', e.target.value)} placeholder="Description (ex. Prestation de recrutement — Data Engineer)" style={{ ...INPUT, padding: '8px 10px', fontSize: '12.5px' }} autoFocus={i === 0} />
              <input value={l.quantite} onChange={e => setLigne(i, 'quantite', e.target.value)} type="number" min="0" step="0.5" title="Quantité" style={{ ...INPUT, ...mono, padding: '8px 8px', fontSize: '12.5px', textAlign: 'right' }} />
              <input value={l.prix_unitaire} onChange={e => setLigne(i, 'prix_unitaire', e.target.value)} type="number" min="0" placeholder="Prix unit." title="Prix unitaire (CHF)" style={{ ...INPUT, ...mono, padding: '8px 8px', fontSize: '12.5px', textAlign: 'right' }} />
              <span style={{ ...mono, fontSize: '12px', color: 'var(--text-2)', textAlign: 'right' }}>{chfAmt((Number(l.quantite) || 0) * (Number(l.prix_unitaire) || 0))}</span>
              <button onClick={() => setForm(p => ({ ...p, lignes: p.lignes.filter((_, j) => j !== i) }))} disabled={lignes.length === 1}
                className="src-row-hover" title="Retirer la ligne"
                style={{ ...GHOST_BTN, width: '28px', height: '28px', padding: 0, justifyContent: 'center', opacity: lignes.length === 1 ? 0.4 : 1 }}><BI icon="x" style={{ fontSize: '11px' }} /></button>
            </div>
          ))}
        </div>
        <button onClick={() => setForm(p => ({ ...p, lignes: [...p.lignes, { description: '', quantite: '1', prix_unitaire: '' }] }))}
          className="src-row-hover" style={{ ...GHOST_BTN, marginTop: '8px', padding: '6px 11px', fontSize: '12px' }}>
          <BI icon="plus-lg" style={{ fontSize: '10px' }} /> Ajouter une ligne
        </button>

        <div style={{ marginTop: '14px', padding: '11px 14px', borderRadius: '10px', background: 'var(--code-bg)', display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {[['Total HT', totalHT], [`TVA ${Number(form.tva_taux) || 0} %`, tvaMontant]].map(([k, v]) => (
            <div key={k} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12.5px', color: 'var(--text-2)' }}>
              <span>{k}</span><span style={mono}>{chfAmt(v)}</span>
            </div>
          ))}
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', fontWeight: 700, color: 'var(--text-strong)', borderTop: '1px solid var(--hair)', paddingTop: '6px', marginTop: '2px' }}>
            <span>Total TTC</span><span style={mono}>{chfAmt(totalTTC)}</span>
          </div>
        </div>

        <div style={{ marginTop: '11px' }}>
          <div style={{ ...LBL, marginBottom: '5px' }}>Note (pied de facture)</div>
          <input value={form.note} onChange={e => setForm(p => ({ ...p, note: e.target.value }))} placeholder="Ex. Payable à 30 jours — merci de rappeler le n° de facture" style={{ ...INPUT, fontSize: '12.5px' }} />
        </div>

        <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <button onClick={() => setForm(null)} className="src-row-hover" style={GHOST_BTN}>Annuler</button>
          <button onClick={save} disabled={busy} style={{ ...PRIMARY_BTN, opacity: busy ? 0.6 : 1 }}>
            <BI icon={busy ? 'arrow-repeat' : 'receipt'} /> {busy ? 'Création…' : 'Créer la facture'}
          </button>
        </div>
      </div>
    </>
  );
}
